import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"

import { prefersReducedMotion } from "../../lib/prefers-reduced-motion"

import { mostSeriousMessage } from "./status_messages"

import { getConsumer } from "@shared/lib/action-cable/consumer"

/** What the device panel can ask the engine for. */
const DEVICE_BRIDGE_EVENTS = ["device:detach", "device:remove"]

/**
 * An ISO8601 moment, in the language the page is already in.
 *
 * `toLocaleString` with the document's own `lang` rather than a format written
 * here: a date written by hand is a date in one language, and this app ships
 * twelve. The browser owns the ordering, the separators and the clock.
 *
 * Null in, null out — and null out for anything unparseable too, because a
 * device that has never spoken and one whose timestamp arrived malformed are
 * both "nothing to show", and `Invalid Date` on screen is worse than a blank.
 */
function formatMoment(iso) {
  if (!iso) return null

  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null

  return at.toLocaleString(document.documentElement.lang || undefined)
}

/**
 * A unit's battery, as the device panel and the run's device list show it: its
 * millivolts, the reading the leg's panel and the Battery view show too, or a
 * SmartCap's percentage when it has none (its millivolts never reach Rails).
 * A RackEye has no percentage, so a panel that knew only that read "—" for
 * every one. Null when the unit has reported neither. Exported for its test.
 */
export function formatBattery(facts) {
  if (typeof facts?.batteryMillivolts === "number") {
    const lang = document.documentElement.lang || undefined
    return `${new Intl.NumberFormat(lang).format(facts.batteryMillivolts)} mV`
  }
  if (typeof facts?.batteryLevel === "number") return `${facts.batteryLevel}%`
  return null
}

/**
 * What the layer sidebar can tell the engine.
 *
 * Listed rather than inlined so adding a message is one edit and cannot be
 * added to the listener without also being added to the teardown.
 */
const SUBSET_BRIDGE_EVENTS = [
  "subsets:container-changed",
  "subsets:order-changed",
  "subsets:content-version",
  "subsets:ensure-boundary",
  "subsets:boundary-added",
  "subsets:known-subsets",
  "subsets:entity-flag",
  "subsets:assign-entity",
  "subsets:cascade-flag",
  "subsets:remove-boundary",
]

/**
 * THE EDITOR'S RIGHT RAIL — one slot, one panel in it (#5970).
 *
 * Every panel on the right occupies the same 384px slot, so which one is open
 * is decided in exactly one place: this table and the four methods under
 * "The right rail" at the bottom of the class. Hand-written rules per panel,
 * each knowing about a different subset of the others, is how two panels end
 * up open at once and pushed past the right edge.
 *
 * `kind` is the only thing the rules turn on:
 *   - "selection" opens because you clicked something on the plan
 *   - "toggle"    opens because you pressed a button
 * A press beats a selection that is already showing; a selection afterwards
 * takes the rail back. Nothing else is a rule.
 *
 * A panel absent from the page is simply skipped: every lookup goes through
 * `#railTarget`, because Stimulus RAISES on a missing target rather than
 * answering undefined.
 */
// The least width left beside a rail panel that is still worth framing an
// entity into — below it, `selectFromInfo` frames against the whole stage.
const MIN_FRAMING_WIDTH_PX = 240

const RIGHT_RAIL = [
  {
    key: "properties",
    panel: "propertiesPanel",
    button: null,
    kind: "selection",
  },
  { key: "racking", panel: "rackingPanel", button: null, kind: "selection" },
  { key: "info", panel: "infoPanel", button: "infoButton", kind: "toggle" },
]

/**
 * Show a select's value without anyone having chosen it: through Tom Select
 * when it has enhanced the select (silently, so no change fires), on the
 * element otherwise.
 */
function setSelectValue(select, value) {
  if (select.tomselect) select.tomselect.setValue(value, true)
  else select.value = value
}

/** Every `%{key}` in a translated sentence, the value taken as it is (no `$&`). */
function fill(template, key, value) {
  return template.replaceAll(`%{${key}}`, () => String(value))
}

/** The values a dialog's sentence names — what the reader has to pick out. */
const NAMED = new Set(["aisle", "displaced"])

/**
 * A translated sentence as nodes, every name in it in `<strong>`: an aisle
 * called "aisle 2 demo 02" otherwise runs into the words around it. Text
 * nodes only, so a name is never read as markup.
 */
function sentence(template, values) {
  const text = template ?? ""
  const nodes = []
  let last = 0
  for (const match of text.matchAll(/%\{(\w+)\}/g)) {
    const [placeholder, key] = match
    nodes.push(document.createTextNode(text.slice(last, match.index)))
    last = match.index + placeholder.length
    if (!(key in values)) {
      nodes.push(document.createTextNode(placeholder))
      continue
    }
    const value = String(values[key])
    if (NAMED.has(key)) {
      const name = document.createElement("strong")
      name.textContent = value
      nodes.push(name)
    } else {
      nodes.push(document.createTextNode(value))
    }
  }
  nodes.push(document.createTextNode(text.slice(last)))
  return nodes
}

/** The face on the other side of a rack. */
function oppositeFace(side) {
  return side === "front" ? "back" : "front"
}

/**
 * The visible label of one of a select's options — Tom Select's own first:
 * options it was handed after the page rendered have no `<option>` element.
 */
function optionLabel(select, value) {
  const enhanced = select.tomselect?.options?.[value]?.text
  if (enhanced) return String(enhanced).trim()

  const option = Array.from(select.options).find((o) => o.value === value)
  return option?.textContent?.trim() ?? ""
}

/** How many real choices a select offers — its empty option aside. */
function optionCount(select) {
  const ts = select.tomselect
  const values = ts
    ? Object.keys(ts.options)
    : Array.from(select.options, (option) => option.value)
  return values.filter((value) => value !== "").length
}

/**
 * Replace a select's options with a flat `[[label, value]]` list, keeping the
 * empty option and whatever is selected if it is still offered.
 */
function setFlatOptions(select, options) {
  const current = select.value
  const ts = select.tomselect
  if (ts) {
    ts.clearOptions((_option, value) => value === "")
    for (const [label, value] of options) ts.addOption({ value, text: label })
    ts.refreshOptions(false)
  } else {
    const empty = select.querySelector('option[value=""]')
    select.replaceChildren(
      ...(empty ? [empty] : []),
      ...options.map(([label, value]) => new Option(label, value)),
    )
  }
  setSelectValue(
    select,
    options.some(([, value]) => value === current) ? current : "",
  )
}

/**
 * Replace a select's options, grouped `[{group, options: [[label, value]]}]`,
 * without firing anything: through Tom Select when it enhanced the select,
 * as `<optgroup>`s otherwise. The empty option stays first.
 */
function setSelectOptions(select, groups) {
  const ts = select.tomselect
  if (ts) {
    ts.clear(true)
    // Its default keeps only the selected options — none, once cleared.
    ts.clearOptions((_option, value) => value === "")
    ts.clearOptionGroups()
    for (const group of groups) {
      ts.addOptionGroup(group.group, { label: group.group })
      for (const [label, value] of group.options) {
        ts.addOption({ value, text: label, optgroup: group.group })
      }
    }
    ts.refreshOptions(false)
    return
  }

  const empty = select.querySelector('option[value=""]')
  select.replaceChildren(...(empty ? [empty] : []))
  for (const group of groups) {
    const optgroup = document.createElement("optgroup")
    optgroup.label = group.group
    for (const [label, value] of group.options) {
      optgroup.append(new Option(label, value))
    }
    select.append(optgroup)
  }
}

/**
 * The count of uprights two aisles joined on one rack will both have, or null
 * when neither changes — the larger of the two, which the Sync gives the
 * shorter (`RegistrationPlan#align_shared_racks`). `uprights` is aisle id →
 * count.
 */
function sharedUprights(uprights, aisleId, partnerId) {
  if (!partnerId || partnerId === aisleId) return null
  const mine = uprights?.[aisleId]
  const theirs = uprights?.[partnerId]
  if (typeof mine !== "number" || typeof theirs !== "number") return null

  return mine === theirs ? null : Math.max(mine, theirs)
}

/**
 * One aisle of the face dialog's list, from the dialog's row template: its
 * name, the badge saying where it ends up — the primary one for the aisle the
 * change is about, the quiet one for the rest; the other is dropped — and its
 * facts, joined as the Sync's list joins them.
 */
function faceDialogRow(template, row) {
  const item = template.content.firstElementChild.cloneNode(true)
  item.querySelector("[data-face-dialog-row-name]").textContent = row.name
  for (const badge of item.querySelectorAll("[data-face-dialog-row-badge]")) {
    const primary = badge.dataset.faceDialogRowBadge === "primary"
    if (primary === row.primary && row.badge) badge.textContent = row.badge
    else badge.remove()
  }
  item.querySelector("[data-face-dialog-row-details]").textContent =
    row.details.join(" · ")
  return item
}

/** `Floorplans::PlanChangeDialogsComponent`'s two dialogs. */
// The tools the bar offers while the plan is only looked at — the keyboard is
// narrowed the same way (`FLOORPLAN_VIEW_TOOLS` in `floorplan-shortcuts.ts`).
const VIEW_TOOLS = ["select", "pan"]

const FACE_DIALOG_ID = "floorplan-face-change-dialog"
const SYNC_DIALOG_ID = "floorplan-sync-dialog"
// `Floorplans::PdfPreviewComponent::DIALOG_ID`.
const PDF_PREVIEW_ID = "floorplan-pdf-preview"
// How long the Sync waits for the save it asked for before saying it could
// not: a save that cannot be made reports nothing at all.
const SYNC_SAVE_WAIT_MS = 15_000

/**
 * Which of its four states a face is in: `disabled` (not one of the run's
 * managed faces — against a wall, it carries nothing), `aisle` (an aisle
 * stands on it, `filed` by facilities), `new` (enabled, no aisle yet, with
 * RackEyes put on it — the Sync creates its aisle) or `free` (enabled, no
 * aisle, nothing on it).
 */
function faceStateOf(aisle, filed) {
  if (!aisle.managed) return "disabled"
  if (filed) return "aisle"
  return aisle.pendingCount > 0 ? "new" : "free"
}

/**
 * Floorplan Canvas Controller — the Drawing v2 editor, Step 1
 * (see `cositas/flloorplan-iot-extraccion.md`).
 *
 * Boots the PIXI drawing engine on a plain ERB page, with no React anywhere,
 * and wires the chrome around it: the toolbar, the properties panel, the layer
 * tree (subsets), the device panel and live telemetry. No catalog, no barriers,
 * no breakdown, no 3D — those stay out of the floorplan-IoT extraction and this
 * controller does not wire them.
 *
 * Almost everything the panel does is client-side. An edit is canvas state;
 * it reaches the database through the same change tracker and autosave as
 * every other edit, so there is no round-trip and no Turbo frame to refresh.
 * Two things are the server's: Sync, and which aisle stands on a face of a
 * rack. Each posts a plain form and the editor reloads with the result.
 *
 * The engine is loaded via dynamic `import()` inside connect() so it lands in
 * its own Vite chunk (vite.config.ts already isolates PIXI into `vendor-pixi`).
 * This is not optional hygiene: app/javascript/controllers/index.js eagerly
 * imports every controller, so a top-level import here would bake the whole
 * canvas graph into the single application.js served to EVERY Hotwire page.
 */
export default class extends Controller {
  static targets = [
    "barrierToast",
    "stage",
    "rail",
    "zoomLevel",
    "status",
    "statusBox",
    "saveIndicator",
    "syncBadge",
    "propertiesPanel",
    "propertiesTitle",
    "propertiesId",
    "undoButton",
    "redoButton",
    "sidebar",
    "infoPanel",
    "infoButton",
    "pdfButton",
    "pdfPreview",
    "pdfViewerTemplate",
    "rackingPanel",
    "rackingName",
    "rackingBayCount",
    "rackingLegCount",
    "rackingLegFiledCount",
    "rackingLength",
    "rackingBayWidth",
    "rackingFrameDepth",
    "rackingAisle",
    "rackingLegTemplate",
    "deviceList",
    "deviceEmpty",
    "deviceRowTemplate",
  ]
  static values = {
    drawingId: String,
    drawingName: String,
    loadErrorMessage: String,
    // A floorplan is born `content_migrated: false` (JSONB) — see
    // `Floorplans::Create`'s class comment — so this is always false here.
    // Kept as a value rather than a constant because `EditorApp` supports
    // both hydration branches and a later step may need the other one.
    contentMigrated: Boolean,
    spaceGroupId: String,
    // The drawing's layers, so the canvas's boundary index has something to
    // read.
    subsets: Array,
    // The saved canvas state. `EditorApp`'s JSONB branch is guarded on this
    // being present, so an editor mounted without it opens empty however much
    // the drawing has stored.
    content: Object,
    // Whether this tenant has `floorplan.iot_telemetry`. A page rendered with
    // it off never fetches and never subscribes — the endpoint and the channel
    // refuse it on the same check, so the two halves cannot disagree.
    telemetryEnabled: Boolean,
    // Gen4 barriers and SmartCaps (`iot.smartcap_barriers`). Off, the canvas
    // wires none of it — no catalog request, no Barrier or SmartCap tool.
    smartcapBarriers: Boolean,
    // The gen4 barriers the tenant's catalog has (`[{code, itemId, name}]`)
    // and where their drawable data is served. Absent with barriers off.
    barrierProducts: Array,
    barrierCatalogPath: String,
    // Why the Barrier menu cannot arm a barrier, by offer state.
    barrierUnavailableTexts: Object,
    deviceStatusPath: String,
    // Where the plan prints as a PDF in the view it shows, and what the status
    // line says meanwhile or when it cannot (`exportPdf`). Absent, no button.
    pdfExportPath: String,
    pdfTexts: Object,
    // What the plan's devices and legs show (`view-modes/`): the view the page
    // opens in, where its data comes from and which datasets this person may
    // read. Absent with telemetry off, and the plan then draws as it always has.
    viewModes: Object,
    // Where the device panel's two links point. Absent means "no link", which
    // is how a device nobody has installed renders: the row is hidden rather
    // than offering a dead href.
    iotAssetUrl: String,
    iotSensorUrl: String,
    detachRefusedMessage: String,
    // Said when a delete kept back something installed — see
    // `floorplan-delete-gate.ts`. The server refuses the same saves.
    deleteRefusedMessage: String,
    // Said when a RackEye taken off the plan leaves its leg in facilities.
    legKeptMessage: String,
    // Removing a leg on the plan, for the Sync to remove from facilities:
    // whether this person may, why each leg that may not be removed must stay
    // (asset id → reasons, strongest first), and what is said about it.
    canRemoveLegs: Boolean,
    legHistory: Object,
    legRemovalTexts: Object,
    // Why a rack's count of uprights cannot be what was asked.
    uprightsTexts: Object,
    // Said when a bay width or frame depth is not a whole number above zero.
    dimensionInvalidMessage: String,
    // Asked before a Turbo visit takes somebody off a plan with unsaved work.
    // `beforeunload` covers a tab close; a same-document visit is not an unload
    // the browser asks about on our behalf.
    leaveUnsavedMessage: String,
    // Sync from facilities and a change to a face of a rack are made on the
    // server and answer with the plan as it now is, applied in place
    // (`adoptPlan`); so they are refused while there is unsaved work, and
    // confirmed otherwise (`PlanChangeDialogsComponent`). `planStatePath` is
    // where the plan comes from when ANOTHER editor changed it.
    syncPath: String,
    // What a Sync would create in facilities for the plan's RackEyes, shown
    // in its dialog before it does (`Floorplans::SyncPreviewComponent`).
    syncPreviewPath: String,
    bindFacePath: String,
    planStatePath: String,
    syncUnsavedMessage: String,
    // `drawings.content_version` as the page was rendered, after the
    // open-time sync. Handed to the engine so its saves are checked against
    // it, and compared with a `plan_synced` broadcast's to tell whether this
    // tab is behind.
    contentVersion: Number,
    // Said when a save is refused because the plan moved on since this tab
    // loaded it, and when another tab's sync changed it underneath unsaved work.
    staleSaveMessage: String,
    planSyncedMessage: String,
    // A change made elsewhere, applied in place (`#refreshPlan`).
    planChangedElsewhereMessage: String,
    // Where facilities files each racking run: its zone, and the aisle each
    // face became — `Floorplans::RunFacilities`, keyed by run id. The contents
    // panel groups runs by it; a run absent from it is drawn, not registered.
    runFacilities: Object,
    // The barriers registered in facilities (barrier ids): their name and
    // zone are facilities', so the barrier panel shows them, not to change.
    linkedBarriers: Array,
    // Which face of which rack each aisle of the floor stands on (aisle id →
    // `{runId, side}`), so the dialog says first where an aisle comes from and
    // what it displaces; each one's count of uprights, so it says what count
    // the two aisles of a rack will share; and its own name, zone and count of
    // legs (`{name, zone, legs}`), for the dialog's row of it. And what the
    // face dialog says for each kind of change (`faceDialogTexts`).
    aisleFaces: Object,
    aisleUprights: Object,
    aisleFacts: Object,
    faceDialogTexts: Object,
    // Whether the RackEye tool is offered (the page's `can_register`), and
    // what is said when a click of it places nothing, by reason.
    canRegister: Boolean,
    // Somebody who may only look opens a canvas that changes nothing
    // (`readOnly`); one who may edit the layout but not the RackEyes moves
    // racks and never a RackEye (`devicesLocked`), and is told so when a
    // RackEye goes back or is not deleted. The server refuses the same saves.
    readOnly: Boolean,
    devicesLocked: Boolean,
    devicesLockedMessage: String,
    // Everybody else opens the plan to look at it, and edits it once they ask
    // (`startEditing`): read-only until then exactly as above, but in place —
    // the canvas, its live state and the view are kept. `?mode=edit` opens it
    // edited. Stopping is refused with work unsaved (`modeSaveFirstMessage`).
    editing: Boolean,
    modeSaveFirstMessage: String,
    // Whether this person may grow an aisle (`facility.update`): a face change
    // that would is refused before its dialog, as `BindFace` refuses it.
    canGrowAisles: Boolean,
    faceGrowRefusedMessage: String,
    rackeyeRefusedTexts: Object,
    // Why a SmartCap was not put on a post (`device-placement`).
    smartcapRefusedTexts: Object,
    // What is said when there is unsaved work.
    faceUnsavedMessage: String,
    faceDisableRefusedMessage: String,
  }

  async connect() {
    // Turbo can fire connect() twice for one logical page (cache restore), and
    // EditorApp is a tsyringe @singleton(). Guard so the second pass does not
    // mount a second canvas onto a stage that already has one.
    if (this.mounted || this.mounting) return
    this.mounting = true

    this.statusMessages = { load: "", save: "", edit: "", export: "" }

    let mountCanvas
    try {
      ;({ mountCanvas } =
        await import("@/domains/orders/lib/canvas/hotwire/mount-canvas"))
    } catch (error) {
      // A chunk-load failure would otherwise leave a blank stage and an
      // unhandled rejection. Surface it instead.
      console.error("[floorplan-canvas] failed to load the canvas chunk", error)
      this.#setStatus("load", this.loadErrorMessageValue)
      // The overlay must not outlive the attempt: the status line is the whole
      // answer, and a spinner over it would say the page is still trying.
      this.#bootFailed()
      this.mounting = false
      return
    }

    // The chunk is here — usually the longest part of a cold open, and the
    // only step the CONTROLLER owns. Everything after it is the engine's, and
    // comes back through `boot-progress`.
    this.#bootStep("editor")

    // disconnect() may have run while the chunk was in flight (fast Turbo
    // navigation). Bail rather than mounting into a detached element.
    if (!this.element.isConnected) {
      this.mounting = false
      return
    }

    try {
      this.mounted = await mountCanvas({
        element: this.stageTarget,
        drawingId: this.drawingIdValue,
        drawingName: this.drawingNameValue,
        contentMigrated: this.contentMigratedValue,
        spaceGroupId: this.spaceGroupIdValue || undefined,
        subsets: this.subsetsValue,
        content: this.contentValue,
        contentVersion: this.hasContentVersionValue
          ? this.contentVersionValue
          : undefined,
        rackEyeTool: this.canRegisterValue,
        readOnly: this.readOnlyValue,
        editing: this.editingValue,
        devicesLocked: this.devicesLockedValue,
        // Asked at each delete, so what the server said last is what holds.
        legRemoval: () => ({
          allowed: this.canRemoveLegsValue,
          historyOf: (assetId) => this.legHistoryValue?.[assetId] ?? [],
        }),
        viewModes: this.#viewModeOptions(),
        pdfExport: this.hasPdfExportPathValue
          ? {
              post: (body) => this.#postPdf(body),
              holdPushes: () => this.#holdPushes(),
              contents: () => this.#planContents(),
            }
          : null,
        barriers:
          this.smartcapBarriersValue && this.hasBarrierCatalogPathValue
            ? {
                catalogPath: this.barrierCatalogPathValue,
                products: this.barrierProductsValue,
                smartCapTool: this.canRegisterValue,
              }
            : null,
      })
    } catch (error) {
      console.error("[floorplan-canvas] the canvas failed to start", error)
      this.#setStatus("load", this.loadErrorMessageValue)
      this.#bootFailed()
      return
    } finally {
      this.mounting = false
    }

    // Mounted after we awaited — the element may have gone away meanwhile.
    if (!this.element.isConnected) {
      this.mounted.destroy()
      this.mounted = null
      return
    }

    this.#setStatus("load", "")

    // The racking panel follows the canvas selection. The rail is decided HERE
    // and nowhere else: `#renderRacking` is also called when the data under a
    // selected run changes — a device heartbeat, the status fetch — and a
    // re-render that re-decided the rail would close the contents panel under
    // somebody reading it, on a timer.
    // What a delete kept back because it is installed. Said AFTER the rest of
    // that delete has run: deleting what it could clears the selection, a
    // selection change clears the edit line, and both happen synchronously —
    // written at once, the sentence would be wiped before anybody read it.
    this.unsubscribeDeleteGuard = this.mounted.editor.onDeleteRefused(
      (_count, _ids, reason) => {
        const message =
          reason === "devices_locked"
            ? this.devicesLockedMessageValue
            : this.deleteRefusedMessageValue
        queueMicrotask(() => this.#setStatus("edit", message))
      },
    )

    // A RackEye taken off a leg facilities has: what happens to the leg —
    // marked for removal on the next Sync, or kept, and why — is said.
    this.unsubscribeLegsKept = this.mounted.editor.onLegsKept?.((detail) => {
      const message = this.#legsLeftMessage(detail)
      if (message) queueMicrotask(() => this.#setStatus("edit", message))
    })

    // Why a click of the RackEye tool placed nothing.
    // The Barrier menu: what may be drawn is known once the canvas has loaded
    // its catalog, and changes nothing else on the page.
    if (this.mounted.barriers) {
      this.#paintBarrierOffers(this.mounted.barriers.offers())
      this.unsubscribeBarrierOffers = this.mounted.barriers.onOffers((offers) =>
        this.#paintBarrierOffers(offers),
      )
      this.unsubscribeBarrierErrors = this.mounted.barriers.onError((message) =>
        this.#toastBarrier(message),
      )
    }

    this.unsubscribePlacement = this.mounted.onPlacementRefused?.((reason) => {
      const message =
        this.rackeyeRefusedTextsValue?.[reason] ??
        this.smartcapRefusedTextsValue?.[reason]
      if (message) this.#setStatus("edit", message)
    })

    this.unsubscribeRacking = this.mounted.racking.onSelection((state) => {
      this.#selectPanel("racking", state !== null)
      this.#renderRacking(state)
    })

    // Fires immediately with the current state, so a canvas that mounts already
    // dirty says so rather than claiming to be saved until the next edit.
    this.unsubscribeSave = this.mounted.save.onSaveStatus((status) => {
      this.#renderSaveStatus(status)
    })

    // The footer's zoom readout. De-duplicated on the whole number inside the
    // seam, so this writes the DOM when the percentage changes and not once per
    // animation frame of a zoom-to-entity.
    this.unsubscribeZoom = this.mounted.editor.onZoomChange((percentage) => {
      this.#renderZoom(percentage)
    })

    this.#wireBoot()

    // Looked at, the sidebar shows its layers: a link that asked for its
    // Products tab is answered with the tab that is offered.
    if (!this.readOnlyValue && !this.editingValue) this.#showLayersTab()

    // The toolbar renders ENGINE state, so it subscribes rather than guessing
    // from the click it handled. Tools change without a click all the time:
    // every keyboard tool key goes straight to the engine.
    this.unsubscribeTool = this.mounted.editor.onToolChange((tool) => {
      this.#renderActiveTool(tool)
    })

    // An export that fails reports it on the bus and nowhere else, so without
    // this the user clicks Export as PDF, no file arrives, and the page says
    // nothing at all.
    this.unsubscribeExport = this.mounted.editor.onExportError((error) => {
      this.#setStatus("export", error)
    })

    // And clears it when one finally works — an export error that outlives the
    // successful retry is the same lie in the other direction.
    this.unsubscribeExportDone = this.mounted.editor.onExportComplete(() => {
      this.#setStatus("export", "")
    })

    this.unsubscribeHistory = this.mounted.editor.onHistoryChange((state) => {
      this.#renderHistory(state)
      // An undo or redo changes the run under an open racking panel without
      // a selection change — a leg's removal mark, say — so it reads it again.
      this.#refreshRacking()
    })

    // Fires immediately with whatever is selected, so a canvas restored by
    // Turbo with a selection opens showing it rather than showing nothing.
    this.unsubscribeProperties = this.mounted.properties.onSelection(
      (selection) => {
        this.#renderProperties(selection)
        this.#setStatus("edit", "")
      },
    )

    // The layer tree's messages to the engine, forwarded.
    //
    // The sidebar is its own Stimulus controller and must render without a
    // canvas, so it dispatches DOM events rather than reaching into the mounted
    // handle. Everything it can cause that the engine has to know about goes
    // through here — a container's flags, the sibling order, and the version
    // the subsets API bumps behind the engine's back.
    this.boundSubsetsBridge = (event) => {
      if (!this.mounted) return
      const { detail, type } = event

      if (type === "subsets:container-changed") {
        this.mounted.subsets.setContainerState(detail.container, {
          isVisible: detail.visible,
          isLocked: detail.locked,
        })
      } else if (type === "subsets:order-changed") {
        this.mounted.subsets.setOrder(detail.order)
      } else if (type === "subsets:content-version") {
        this.mounted.subsets.adoptContentVersion(detail.version)
      } else if (type === "subsets:ensure-boundary") {
        this.mounted.subsets.ensureBoundary(detail)
      } else if (type === "subsets:boundary-added") {
        this.mounted.subsets.addBoundary(detail)
      } else if (type === "subsets:known-subsets") {
        this.mounted.subsets.setKnownSubsets(detail.ids)
      } else if (type === "subsets:assign-entity") {
        this.mounted.subsets.assignEntity(detail.entityId, detail.subsetId)
      } else if (type === "subsets:cascade-flag") {
        this.mounted.subsets.cascadeFlag(
          detail.parentIds,
          detail.flag,
          detail.value,
        )
      } else if (type === "subsets:remove-boundary") {
        this.mounted.subsets.removeBoundaryShape(detail.subsetId)
      } else if (type === "subsets:entity-flag") {
        this.mounted.subsets.setEntityFlag(
          detail.entityId,
          detail.flag,
          detail.value,
        )
      }
    }
    for (const name of SUBSET_BRIDGE_EVENTS) {
      this.element.addEventListener(name, this.boundSubsetsBridge)
    }

    // The other direction, and the only thing that goes that way: the canvas
    // telling the sidebar which shape it just drew. Without it the rectangle
    // appears and the row's `bounds_entity_id` stays null for ever — the group
    // looks drawn until the next reload, when its outline is simply gone.
    this.unsubscribeEntityRows = this.mounted.subsets.onEntityRows((rows) => {
      this.dispatch("entity-rows", {
        detail: { rows },
        prefix: "subsets",
        bubbles: true,
      })
    })

    this.unsubscribeBoundary = this.mounted.subsets.onBoundaryCreated(
      (created) => {
        this.dispatch("boundary-created", {
          detail: created,
          prefix: "subsets",
          bubbles: true,
        })
      },
    )

    this.#wireDevice()
    this.#wireViewModes()
    // Not awaited: the initial status fetch is a round trip, and holding
    // `connect()` open for it would delay every subscription above it for a
    // plan that draws perfectly well in its plan-side colours meanwhile.
    void this.#wireTelemetry()

    // Only while there is something to lose. An unconditional guard trains
    // people to dismiss it, and then it is not a guard.
    // Never while the plan is only looked at: nothing held then can be saved.
    this.boundBeforeUnload = (event) => {
      if (this.#locked || this.saveState === "saved") return
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", this.boundBeforeUnload)

    // The engine takes its shortcuts from `window`, so a key pressed in an
    // open dialog reached the plan under it too: Escape, meant to cancel a
    // face change, also deselected the rack the dialog was about, and Delete
    // would have deleted it. Stopped at `document` — after the dialog's own
    // handlers, before the engine's.
    this.boundKeepDialogKeys = (event) => {
      // And while a change of ours is out: the editor is inert to the
      // pointer, not to keys — Delete or an undo then made the plan dirty
      // under the answer, which was refused.
      if (
        this.#posting ||
        (event.target instanceof Element &&
          event.target.closest("dialog[open]"))
      )
        event.stopPropagation()
    }
    for (const type of ["keydown", "keyup"])
      document.addEventListener(type, this.boundKeepDialogKeys)
  }

  disconnect() {
    this.#closePdf()
    this.unsubscribeBarrierOffers?.()
    this.unsubscribeBarrierOffers = null
    this.unsubscribeBarrierPanel?.()
    this.unsubscribeBarrierPanel = null
    this.unsubscribeBarrierErrors?.()
    this.unsubscribeBarrierErrors = null
    if (this.unsubscribePlacement) {
      this.unsubscribePlacement()
      this.unsubscribePlacement = null
    }
    this.unsubscribeLegsKept?.()
    this.unsubscribeLegsKept = null
    if (this.unsubscribeDeleteGuard) {
      this.unsubscribeDeleteGuard()
      this.unsubscribeDeleteGuard = null
    }
    if (this.unsubscribeRacking) {
      this.unsubscribeRacking()
      this.unsubscribeRacking = null
    }
    if (this.unsubscribeZoom) {
      this.unsubscribeZoom()
      this.unsubscribeZoom = null
    }
    if (this.unsubscribeBoot) {
      this.unsubscribeBoot()
      this.unsubscribeBoot = null
    }
    if (this.unsubscribeHistory) {
      this.unsubscribeHistory()
      this.unsubscribeHistory = null
    }
    if (this.unsubscribeTool) {
      this.unsubscribeTool()
      this.unsubscribeTool = null
    }
    if (this.boundBeforeUnload) {
      window.removeEventListener("beforeunload", this.boundBeforeUnload)
      this.boundBeforeUnload = null
    }
    if (this.boundKeepDialogKeys) {
      for (const type of ["keydown", "keyup"])
        document.removeEventListener(type, this.boundKeepDialogKeys)
      this.boundKeepDialogKeys = null
    }
    if (this.unsubscribeProperties) {
      this.unsubscribeProperties()
      this.unsubscribeProperties = null
    }
    if (this.unsubscribeExport) {
      this.unsubscribeExport()
      this.unsubscribeExport = null
    }
    if (this.unsubscribeExportDone) {
      this.unsubscribeExportDone()
      this.unsubscribeExportDone = null
    }
    if (this.unsubscribeSave) {
      this.unsubscribeSave()
      this.unsubscribeSave = null
    }
    if (this.unsubscribeEntityRows) {
      this.unsubscribeEntityRows()
      this.unsubscribeEntityRows = null
    }
    if (this.unsubscribeBoundary) {
      this.unsubscribeBoundary()
      this.unsubscribeBoundary = null
    }
    if (this.boundSubsetsBridge) {
      for (const name of SUBSET_BRIDGE_EVENTS) {
        this.element.removeEventListener(name, this.boundSubsetsBridge)
      }
      this.boundSubsetsBridge = null
    }
    if (this.unsubscribeDevice) {
      this.unsubscribeDevice()
      this.unsubscribeDevice = null
    }
    if (this.unsubscribeInfo) {
      this.unsubscribeInfo()
      this.unsubscribeInfo = null
    }
    this.planInfoState = null
    if (this.boundDeviceBridge) {
      for (const name of DEVICE_BRIDGE_EVENTS) {
        this.element.removeEventListener(name, this.boundDeviceBridge)
      }
      this.boundDeviceBridge = null
    }
    if (this.telemetryChannel) {
      this.telemetryChannel.unsubscribe()
      this.telemetryChannel = null
    }
    this.unsubscribeViewModes?.()
    this.unsubscribeViewModes = null
    this.unsubscribeViewHover?.()
    this.unsubscribeViewHover = null
    // No consumer.disconnect() here: this.cable is the page's shared consumer,
    // and tearing it down would cut the socket for every other controller.
    this.deviceFactsByPoint = null
    this.selectedDevice = null
    if (this.mounted) {
      this.mounted.destroy()
      this.mounted = null
    }
  }

  // =========================================================================
  // The device panel, and live state (CONEK-6447 / CONEK-5943)
  // =========================================================================

  /**
   * The device panel, wired exactly as the properties panel is.
   *
   * Its absence was the first version's real bug and it was invisible: the
   * component, the controller and the seam were each tested on their own and
   * all three passed, while the panel in the running app showed a dash in every
   * field for ever and the Release button did nothing at all. Nothing crossed
   * the join, so nothing noticed there was no join.
   */
  #wireDevice() {
    const devicePanel = this.mounted?.devicePanel
    if (!devicePanel) return

    // Fires immediately with the current selection, so a panel connected over
    // an already-selected device renders it rather than waiting for a click.
    //
    // REMEMBERED, because the selection is no longer the only thing that
    // changes what the panel says: a heartbeat moves the battery and the
    // last-seen, and the panel is open while it happens.
    // The barrier body (`floorplans--barrier`), with the tenant's barriers on.
    this.unsubscribeBarrierPanel = this.mounted.barrierPanel?.onSelection(
      (state) => this.#publishBarrierState(state),
    )

    this.unsubscribeDevice = devicePanel.onSelection((state) => {
      this.selectedDevice = state
      this.#publishDeviceState()
    })

    this.boundDeviceBridge = (event) => {
      const deviceId = event.detail?.deviceId
      if (!deviceId || this.#locked) return

      // The selection, through the one funnel every delete takes — the gate
      // that keeps a paired RackEye, and one undoable step.
      if (event.type === "device:remove") {
        if (this.selectedDevice?.id === deviceId) {
          this.mounted?.editor?.requestDelete()
        }
        return
      }

      // Through the ATTACHMENT seam, which is the only thing that attaches or
      // releases. The panel is a READ surface: giving it a detach of its own
      // would be a second place that decides whether a mounting point is free.
      //
      // A refusal, not a failure: a unit is paired to the leg, and taking it
      // off is done on site, from the leg (`device-pairing`).
      if (!this.mounted?.devices?.detach(deviceId)) {
        this.#setStatus("edit", this.detachRefusedMessageValue)
      }
    }
    for (const name of DEVICE_BRIDGE_EVENTS) {
      this.element.addEventListener(name, this.boundDeviceBridge)
    }
  }

  // =========================================================================
  // The view modes — what the plan's devices and legs show
  // =========================================================================

  /**
   * What `mountCanvas` is handed for the view modes, or null without them.
   *
   * The fetch is this controller's, so the engine never learns about CSRF or
   * `@rails/request.js`: it is handed a function that answers JSON or throws.
   */
  #viewModeOptions() {
    if (!this.telemetryEnabledValue || !this.hasViewModesValue) return null
    const { initial, dataPath, datasets } = this.viewModesValue
    if (!initial) return null

    return {
      initial,
      dataPath: dataPath ?? "",
      datasets: datasets ?? [],
      fetchJson: async (url) => {
        const response = await get(url, { responseKind: "json" })
        if (!response.ok) throw new Error(`view data ${response.statusCode}`)
        return response.json
      },
    }
  }

  /**
   * The view modes' two announcements, as events on this element for
   * `floorplans--view-mode`, which owns the legend, the tooltip and the URL.
   *
   * The tooltip is told the point's live facts too — the ones this controller
   * keeps for the device panel — so a struck leg can say it is also silent.
   */
  #wireViewModes() {
    const viewModes = this.mounted?.viewModes
    if (!viewModes) return

    this.unsubscribeViewModes = viewModes.onChange((view) => {
      this.dispatch("changed", { prefix: "view-mode", detail: view })
      // New readings may have arrived for the device the panel shows.
      this.#publishDeviceState()
    })
    this.unsubscribeViewHover = viewModes.onHover((tip) => {
      this.dispatch("hover", {
        prefix: "view-mode",
        detail: tip
          ? { ...tip, facts: this.deviceFactsByPoint?.get(tip.pointId) ?? null }
          : null,
      })
    })
  }

  /** A unit's signal and temperature, as values, from the view modes' readings. */
  #viewReadings(pointId) {
    const reading = pointId
      ? this.mounted?.viewModes?.readingsOf(pointId)?.telemetry
      : null
    const number = (value, digits) =>
      new Intl.NumberFormat(document.documentElement.lang || undefined, {
        maximumFractionDigits: digits,
      }).format(value)
    return {
      signal:
        typeof reading?.rssiDbm === "number"
          ? `${number(reading.rssiDbm, 0)} dBm`
          : null,
      temperature:
        typeof reading?.temperatureC === "number"
          ? `${number(reading.temperatureC, 1)} °C`
          : null,
    }
  }

  /** `view-mode:request` — draw the view the menu or the legend asked for. */
  setViewMode(event) {
    const state = event.detail?.state
    if (state) this.mounted?.viewModes?.set(state)
  }

  /** `view-mode:prefetch` — the menu opened: fetch what is cheap to have ready. */
  prefetchViewModes() {
    this.mounted?.viewModes?.prefetch()
  }

  /** `view-mode:retry` — the legend's Retry. */
  retryViewMode() {
    this.mounted?.viewModes?.retry()
  }

  /**
   * What the device panel renders, from the two halves that know it.
   *
   * The SEAM knows the plan: which catalog item this is, what it is fixed to,
   * which aisle and which leg, the code on that leg's label. It reads the
   * canvas and never talks to the server, which is the rule that keeps it
   * honest.
   *
   * THIS CONTROLLER knows the world: the serial, the IPUI, the firmware, the
   * battery, when the unit last spoke, how many alerts stand against it. All of
   * it arrives from `device_status` and from the channel, and all of it is keyed
   * by mounting point — which is why `host.pointId` had to exist.
   *
   * Joined here rather than in either half: putting the server's facts in the
   * seam would break its one rule, and giving the panel the map would make a
   * view do a lookup. The panel gets one flat set of facts and renders it.
   */
  #publishDeviceState() {
    const state = this.selectedDevice ?? null
    const facts = state ? this.#deviceFacts(state) : null

    // The header names the UNIT, by the IPUI on its label, once one is fitted
    // — the one identifier anybody holding the device can read. Written here
    // because the IPUI is a live fact, and it arrives after the selection does.
    if (state && this.hasPropertiesIdTarget) {
      this.propertiesIdTarget.textContent = facts?.ipui ?? ""
    }

    this.dispatch("state", {
      prefix: "device",
      detail: state
        ? { state, facts, links: this.#deviceLinks(state) }
        : { state: null },
      bubbles: true,
    })
  }

  /**
   * Every row of the device panel, as one flat map of field → value.
   *
   * Flat and stringy on purpose: the panel hides a whole section when none of
   * its fields has a value, so "is this known yet" has to be one question with
   * one answer. A number and a null are both answers; `undefined` is not, and a
   * missing key would leave the previous device's value on screen.
   *
   * No user-visible WORD is composed here — every label, and the "awaiting"
   * sentence, is rendered by the server. What this writes is values: a
   * percentage or millivolts, a count, a timestamp in the page's own language.
   */
  #deviceFacts(state) {
    const host = state.host
    const live = host ? this.deviceFactsByPoint?.get(host.pointId) : null

    return {
      product: state.name,
      code: state.productCode,
      kind: state.kind,
      host: host?.name ?? null,
      // A run nobody named — the usual case for a migrated warehouse. The
      // panel says "Racking run" in words it owns, rather than the type key.
      hostUnnamed: Boolean(host) && !host.name,
      // The aisle's NAME, which is empty until somebody gives it one. The panel
      // falls back to the face's own word, which it carries itself.
      aisle: host?.aisle ?? null,
      aisleSide: host?.side ?? null,
      point: host?.pointLabel ?? null,
      scanCode: host?.scanCode ?? null,
      // A leg that IS an asset and has no code yet is not missing data: the
      // code is on a sticker an installer puts on the upright and scans, so
      // every confirmed leg reads this way until somebody stands in front of
      // the rack. The panel says so in words it owns.
      awaitingQr: Boolean(state.facilitiesAssetId) && !host?.scanCode,
      // Millimetres, and LOCAL to the host — the same frame the mounting point
      // is stored in, so what the panel says and what the row holds are one
      // number.
      position: host ? `${host.xMm}, ${host.yMm} mm` : null,
      serialNumber: live?.serialNumber ?? null,
      ipui: live?.ipui ?? null,
      firmware: live?.firmware ?? null,
      battery: formatBattery(live),
      // Whether the unit is reporting, and whether its battery is low: the
      // two facts an `alert` hides behind its one word in the header.
      connection:
        live?.sensorNodeId && typeof live.online === "boolean"
          ? live.online
            ? "online"
            : "offline"
          : null,
      lowBattery: live?.lowBattery === true,
      // What the view modes last read for the unit, once one of them has
      // asked: its signal and its temperature. Absent until then — the panel
      // hides a row with nothing in it.
      ...this.#viewReadings(host?.pointId),
      lastSeen: formatMoment(live?.lastSeen),
      // Zero is not a fact worth a row: a device with no alerts is the normal
      // one, and a column of noughts buries the device that has three.
      alerts: live?.alertCount ? String(live.alertCount) : null,
      warnings: live?.warningCount ? String(live.warningCount) : null,
    }
  }

  /**
   * The two places this device has a page, and null for the ones it has not.
   *
   * The LEG has a page from the moment it is an asset; the UNIT has one only
   * once an installer has fitted something, because a planned device is an
   * intention with no `Iot::SensorNode` row behind it. Hidden rather than
   * disabled at the other end: a link that goes nowhere is worse than no link.
   */
  #deviceLinks(state) {
    const live = state.host
      ? this.deviceFactsByPoint?.get(state.host.pointId)
      : null

    return {
      asset: this.#legHref(state.facilitiesAssetId),
      sensor: this.#sensorHref(live?.sensorNodeId),
    }
  }

  /**
   * A leg's page in IoT, or null while there is none to open: the leg is not
   * an asset yet, or the page carries no URL to build it from. The one place
   * the link is spelled — the device panel, the racking panel and Info all ask
   * here, so they cannot drift apart.
   */
  #legHref(assetId) {
    return assetId && this.hasIotAssetUrlValue
      ? `${this.iotAssetUrlValue}/${assetId}`
      : null
  }

  /** A fitted unit's page in IoT, on the same terms as `#legHref`. */
  #sensorHref(sensorNodeId) {
    return sensorNodeId && this.hasIotSensorUrlValue
      ? `${this.iotSensorUrlValue}/${sensorNodeId}`
      : null
  }

  /**
   * One point's live facts, merged rather than replaced.
   *
   * A telemetry frame carries what the heartbeat changed and nothing else — no
   * IPUI, no serial, no node id — so a plain `set` would erase the identity the
   * initial fetch established the first time a device so much as breathed.
   * `undefined` is dropped for the same reason: the two broadcasters send
   * different subsets, and the narrower one must not blank what it omits.
   */
  #mergeDeviceFacts(pointId, patch) {
    if (!pointId) return

    this.deviceFactsByPoint ??= new Map()
    const merged = { ...(this.deviceFactsByPoint.get(pointId) ?? {}) }
    for (const [key, value] of Object.entries(patch)) {
      if (value !== undefined) merged[key] = value
    }
    this.deviceFactsByPoint.set(pointId, merged)

    this.#publishDeviceState()
    this.#publishInfo()
    this.#refreshRacking()
  }

  // =========================================================================
  // The racking panel
  // =========================================================================

  /**
   * Fill the panel for the selected run, or close it on null.
   *
   * It does NOT open the rail — see the selection seam in `connect()`. Every
   * word is pre-rendered in the panel; this writes numbers, names and ids and
   * picks which state shows.
   */
  #renderRacking(state) {
    if (!this.hasRackingPanelTarget) return

    if (state === null) {
      this.mounted?.racking?.highlightFace?.(null)
      this.rackingSelection = null
      this.#closePanel("racking")
      return
    }

    const placement = this.runFacilitiesValue?.[state.id] ?? null
    // What the face controls act on when one is used.
    this.rackingSelection = {
      runId: state.id,
      aisles: state.aisles ?? [],
      placement,
    }

    if (this.hasRackingNameTarget) {
      this.rackingNameTarget.textContent =
        state.name || this.rackingNameTarget.dataset.unnamed || ""
    }
    if (this.hasRackingBayCountTarget) {
      this.rackingBayCountTarget.textContent = String(state.bayCount)
    }
    if (this.hasRackingLegCountTarget) {
      this.rackingLegCountTarget.textContent = String(state.legCount)
    }
    // The legs that are facilities assets, across both faces — the number
    // the contents panel calls "legs", next to the one this tab does.
    if (this.hasRackingLegFiledCountTarget) {
      this.rackingLegFiledCountTarget.textContent = String(
        (state.aisles ?? []).reduce(
          (sum, aisle) =>
            sum + (aisle.legs ?? []).filter((leg) => leg.assetId).length,
          0,
        ),
      )
    }
    // The units are in the labels beside these, where the locale files own
    // them — and the number reads with the page's own decimal separator.
    if (this.hasRackingLengthTarget) {
      // Metres to one decimal: the run is stored in whole millimetres, but
      // 8.1 m is the number somebody pacing out an aisle is holding.
      this.rackingLengthTarget.textContent = new Intl.NumberFormat(
        document.documentElement.lang || undefined,
        { minimumFractionDigits: 1, maximumFractionDigits: 1 },
      ).format(state.runLengthMm / 1000)
    }
    if (this.hasRackingBayWidthTarget) {
      this.rackingBayWidthTarget.textContent = String(state.bayWidthMm)
    }
    if (this.hasRackingFrameDepthTarget) {
      this.rackingFrameDepthTarget.textContent = String(state.frameDepthMm)
    }

    this.#renderAisles(state.aisles, placement)
    this.#renderDeviceList(state.devices, placement)
    this.#renderZone(state, placement)
    this.#renderUprights(state, placement)
    this.#renderDimensions(state)
  }

  /** The bay width and frame depth fields, unless somebody is typing in one. */
  #renderDimensions(state) {
    for (const input of this.element.querySelectorAll(
      "[data-racking-dimension]",
    )) {
      if (document.activeElement === input) continue
      input.value = String(state[input.dataset.rackingDimension] ?? "")
    }
  }

  /** data-action="change->facilities--floorplan-canvas#setRackDimension" */
  setRackDimension(event) {
    if (this.#locked) return
    if (!this.rackingSelection) return

    const key = event.target.dataset.rackingDimension
    const value = Number(event.target.value)
    if (!Number.isInteger(value) || value <= 0) {
      this.#setStatus("edit", this.dimensionInvalidMessageValue)
      event.target.value = String(this.mounted?.racking?.state?.()?.[key] ?? "")
      return
    }
    this.mounted?.racking?.setDimensions?.({ [key]: value })
    this.#refreshRacking()
  }

  /**
   * How many uprights the rack is, for somebody who may change it: one with
   * an aisle takes the count on the Sync — and changing it is a change to
   * facilities, so it takes the right to edit there and the plan's device
   * key — one without is only a drawing.
   */
  #renderUprights(state, placement) {
    const block = this.element.querySelector("[data-racking-uprights]")
    if (!block) return

    // A rack with an aisle is never a drawing, even for somebody this page
    // sends no counts to: shortening it as one would only be undone by the
    // next pull, loosening its RackEyes. Without its count, no field.
    const faced = this.#hasAisle(placement)
    const may = faced
      ? this.#aisleCountOf(placement) !== null &&
        !this.devicesLockedValue &&
        this.canGrowAislesValue
      : !this.readOnlyValue
    hideOrShow(block, !may)

    const input = block.querySelector("[data-racking-uprights-input]")
    if (input && document.activeElement !== input) {
      input.value = String(
        state.uprights?.planned ?? state.uprights?.count ?? "",
      )
    }
  }

  /** Whether an aisle stands on either face of this rack. */
  #hasAisle(placement) {
    return Object.values(placement?.aisles ?? {}).some(Boolean)
  }

  /** The count of uprights the aisles on this rack have now, or null for none. */
  #aisleCountOf(placement) {
    const counts = Object.values(placement?.aisles ?? {})
      .filter(Boolean)
      .map((aisle) => this.aisleUprightsValue?.[aisle.id])
      .filter((count) => typeof count === "number")
    return counts.length > 0 ? Math.max(...counts) : null
  }

  /** data-action="change->facilities--floorplan-canvas#setUprights" */
  setUprights(event) {
    if (this.#locked) return
    const count = Number.parseInt(event.target.value ?? "", 10)
    if (Number.isNaN(count)) {
      this.#refreshRacking()
      return
    }
    this.#applyUprights(count)
  }

  /** Give the selected rack this many uprights, or say why not. */
  #applyUprights(count) {
    if (!this.rackingSelection) return
    // A guard behind the field `#renderUprights` hides for such a rack: no
    // count to measure it against, so nothing is asked.
    const { placement } = this.rackingSelection
    if (this.#hasAisle(placement) && this.#aisleCountOf(placement) === null) {
      this.#refreshRacking()
      return
    }

    const texts = this.uprightsTextsValue ?? {}
    // Not clamped: a count under one is refused with the minimum, like any
    // count the rack cannot go down to.
    const result = this.mounted?.racking?.setUprights?.(
      count,
      this.#aisleCountOf(this.rackingSelection.placement),
    )
    if (result?.refused === "min") {
      const why =
        result.reason === "permission"
          ? (texts.permission ?? "")
          : (this.legRemovalTextsValue?.kept?.[result.reason] ?? "").replace(
              "%{leg}",
              result.leg,
            )
      this.#setStatus(
        "edit",
        fill(this.#plural(texts.min, result.min), "reason", why).trim(),
      )
    } else if (result?.refused === "max") {
      this.#setStatus("edit", fill(texts.max ?? "", "max", result.max))
    }
    if (result?.refused) {
      // The field still has focus, so the panel leaves it alone: it is put
      // back to what the rack is.
      const input = this.element.querySelector("[data-racking-uprights-input]")
      const state = this.mounted?.racking?.state?.()
      if (input && state) {
        input.value = String(state.uprights?.planned ?? state.uprights?.count)
      }
    }
    this.#refreshRacking()
  }

  /**
   * The zone a rack nobody has registered goes in — only until one of its
   * faces is: from then on its aisles are in theirs.
   */
  #renderZone(state, placement) {
    const block = this.element.querySelector("[data-racking-zone]")
    if (!block) return

    const registered = Object.values(placement?.aisles ?? {}).some(Boolean)
    const select = block.querySelector("[data-racking-zone-select]")
    // A choice only when there is one: a floor with one zone (or none) puts
    // every new aisle in it, or stops the Sync saying so.
    hideOrShow(block, registered || !select || optionCount(select) < 2)
    if (select) setSelectValue(select, state.zoneId ?? "")
  }

  /** data-action="change->facilities--floorplan-canvas#setBarrierZone" */
  setBarrierZone(event) {
    if (this.#locked) return
    const state = this.mounted?.barrierPanel?.setZone(event.target.value ?? "")
    if (state) this.#publishBarrierState(state)
  }

  /** data-action="change->facilities--floorplan-canvas#renameBarrier" */
  renameBarrier(event) {
    if (this.#locked) return
    // For the barrier the field was showing (`floorplans--barrier` keeps it),
    // which the selection may have left by the time this fires (CONEK-8680);
    // the panel then stays on the barrier selected now.
    const panel = this.mounted?.barrierPanel
    const id = event.target.dataset?.barrierId || undefined
    const state = panel?.setName(event.target.value ?? "", id)
    if (state && state.id === panel.state()?.id)
      this.#publishBarrierState(state)
  }

  #publishBarrierState(state) {
    const detail = state
      ? { ...state, linked: this.linkedBarriersValue.includes(state.id) }
      : null
    this.dispatch("state", { prefix: "barrier", detail })
  }

  /** data-action="change->facilities--floorplan-canvas#setRackZone" */
  setRackZone(event) {
    if (this.#locked) return
    if (!this.rackingSelection) return
    this.mounted?.racking?.setZone?.(event.target.value ?? "")
  }

  /** data-action="change->facilities--floorplan-canvas#renameFace" */
  renameFace(event) {
    if (this.#locked) return
    const side = event.target.dataset.rackingFaceName
    if (!side || !this.rackingSelection) return
    const state = this.mounted?.racking?.setAisleName?.(
      side,
      event.target.value ?? "",
    )
    if (state) this.#renderAisles(state.aisles, this.rackingSelection.placement)
  }

  /**
   * The selected run again, from the canvas, when the facts under it changed —
   * an IPUI arriving, a battery reading. Only while the panel is open, and
   * never deciding the rail.
   */
  #refreshRacking() {
    if (!this.hasRackingPanelTarget || this.rackingPanelTarget.hidden) return

    this.#renderRacking(this.mounted?.racking?.state() ?? null)
  }

  #renderAisles(aisles, placement) {
    if (!this.hasRackingAisleTarget) return

    const byFace = new Map((aisles ?? []).map((aisle) => [aisle.side, aisle]))

    for (const block of this.rackingAisleTargets) {
      const side = block.dataset.rackingAisle
      const aisle = byFace.get(side)
      if (!aisle) continue

      // The name facilities filed the face under, over what the plan calls it.
      const filed = placement?.aisles?.[side] ?? null
      const title = block.querySelector("[data-racking-aisle-title]")
      if (title) title.textContent = filed?.name || aisle.name || ""

      // One of three states, and every part of the block shows only in the
      // states it lists — a disabled face says nothing about legs at all.
      const state = faceStateOf(aisle, filed)
      block.dataset.faceState = state
      for (const part of block.querySelectorAll("[data-face-show]")) {
        hideOrShow(part, !part.dataset.faceShow.split(" ").includes(state))
      }

      // How many of its uprights carry a leg.
      const tally = block.querySelector("[data-racking-aisle-tally]")
      if (tally)
        tally.textContent = `${aisle.registeredCount} / ${aisle.legCount}`

      // The uprights its aisle gets on the Sync, to share the rack.
      const grows = block.querySelector("[data-racking-face-grows]")
      if (grows) {
        const growth = this.#uprightGrowth(placement, side)
        grows.textContent = growth
          ? fill(
              fill(grows.dataset.text ?? "", "from", growth.from),
              "to",
              growth.to,
            )
          : ""
        if (!growth) hideOrShow(grows, true)
      }

      // A new aisle on the Sync, or one facilities has: only what the choice
      // needs is shown (`setFaceMode`).
      if (state === "new") this.#applyFaceMode(block, this.#faceMode(side))

      // The name the Sync gives a new aisle: the one typed for this face, or
      // the field left blank with the derived one shown in its place — the
      // rack's name and the face, or the server's own "numbered on sync".
      const name = block.querySelector("[data-racking-face-name]")
      if (name) {
        name.dataset.numbered ??= name.placeholder
        name.placeholder = aisle.name || name.dataset.numbered
        if (document.activeElement !== name) {
          name.value = aisle.named ? aisle.name : ""
        }
      }

      this.#renderAisleLegs(block, aisle)
      this.#renderFaceControls(block, aisle, placement)

      // The aisle's own list of legs in IoT — once facilities has filed it.
      this.#setIotLink(
        block.querySelector("[data-racking-aisle-link]"),
        state === "aisle" && filed?.id && this.hasIotAssetUrlValue
          ? `${this.iotAssetUrlValue}?asset_group_id=${encodeURIComponent(filed.id)}&asset_type=rla`
          : null,
      )
    }
  }

  /**
   * A face's controls, for somebody who may change the plan: the select
   * showing the aisle on it (choosing another swaps them), the switch that
   * enables a face with no aisle, and — when both faces have one — splitting
   * this one off onto a rack of its own. Set without firing anything: these
   * reflect the run, they do not act. Which of them shows is the face's state
   * (`data-face-show`); Separate also needs the other face's aisle.
   */
  #renderFaceControls(block, aisle, placement) {
    const controls = block.querySelector("[data-racking-face-controls]")
    if (!controls) return

    const side = aisle.side
    const filed = placement?.aisles?.[side] ?? null
    const otherSide = side === "front" ? "back" : "front"
    const otherFiled = placement?.aisles?.[otherSide] ?? null

    const select = controls.querySelector("[data-racking-face-select]")
    if (select) setSelectValue(select, filed?.id ?? "")

    const toggle = controls.querySelector("[data-racking-face-toggle]")
    if (toggle) this.#setFaceSwitch(toggle, aisle.managed)

    const separate = controls.querySelector("[data-racking-face-separate]")
    if (separate && filed && !otherFiled) hideOrShow(separate, true)
  }

  /**
   * data-action="click->facilities--floorplan-canvas#setFaceMode"
   *
   * On a face whose RackEyes wait for a new aisle: keep it a new aisle
   * (named here), or put an aisle facilities has on it (chosen here).
   */
  setFaceMode(event) {
    if (this.#locked) return
    event?.preventDefault()
    const button = event.target.closest("[data-racking-face-mode]")
    const block = button?.closest("[data-racking-aisle]")
    const runId = this.rackingSelection?.runId
    if (!button || !block || !runId) return

    const mode = button.dataset.rackingFaceMode
    this.#faceModes.set(`${runId}:${block.dataset.rackingAisle}`, mode)
    this.#applyFaceMode(block, mode)
  }

  // Each face's choice, while the plan is open: run id:side → new | existing.
  #faceModes = new Map()

  #faceMode(side) {
    const runId = this.rackingSelection?.runId
    return this.#faceModes.get(`${runId}:${side}`) ?? "new"
  }

  #applyFaceMode(block, mode) {
    // The look and what a screen reader says, both: `Ui::ButtonComponent`'s
    // `active:` renders `aria-pressed` alongside `data-active`.
    for (const button of block.querySelectorAll("[data-racking-face-mode]")) {
      const pressed = String(button.dataset.rackingFaceMode === mode)
      button.dataset.active = pressed
      button.setAttribute("aria-pressed", pressed)
    }
    for (const part of block.querySelectorAll("[data-face-mode-show]")) {
      hideOrShow(part, part.dataset.faceModeShow !== mode)
    }
  }

  /** The switch says whether the face is ENABLED. */
  #setFaceSwitch(toggle, enabled) {
    const checkbox = toggle.querySelector('input[type="checkbox"]')
    if (!checkbox || checkbox.checked === enabled) return

    // `ui--toggle` redraws its switch on the checkbox's change; the flag keeps
    // `toggleFace` from taking that for somebody's click.
    this.settingFaceSwitch = true
    checkbox.checked = enabled
    checkbox.dispatchEvent(new Event("change", { bubbles: true }))
    this.settingFaceSwitch = false
  }

  /**
   * What a delete did to the legs its RackEyes leave
   * (`entity:delete:legs-kept`): marked for removal on the next Sync, kept
   * because they have history (named, with why), or kept because this
   * person may not remove legs from facilities.
   */
  #legsLeftMessage(detail) {
    const texts = this.legRemovalTextsValue ?? {}
    const lines = []
    if (detail?.marked) lines.push(this.#plural(texts.marked, detail.marked))
    if (detail?.count) {
      const why = texts.kept?.[detail.reason]
      if (why && detail.leg) {
        if (!detail.marked && texts.removed) lines.push(texts.removed)
        lines.push(why.replace("%{leg}", detail.leg))
      } else {
        lines.push(this.legKeptMessageValue)
      }
    }
    return lines.filter(Boolean).join(" ")
  }

  /** The form of `forms` (`{one, other, …}`) this count takes in the page's language. */
  #plural(forms, count) {
    if (!forms) return ""
    if (typeof forms === "string") return forms.replace("%{count}", count)
    const category = new Intl.PluralRules(
      document.documentElement.lang || undefined,
    ).select(count)
    const form = forms[category] ?? forms.other ?? ""
    return form.replace("%{count}", count)
  }

  /** data-action="click->facilities--floorplan-canvas#removeLeg" */
  removeLeg(event) {
    if (this.#locked) return
    this.#setLegRemoval(event, true)
  }

  /** data-action="click->facilities--floorplan-canvas#keepLeg" */
  keepLeg(event) {
    if (this.#locked) return
    this.#setLegRemoval(event, false)
  }

  /**
   * Mark the leg of this row for removal on the next Sync, or keep it. A leg
   * with history is not marked: the line says why, as the Sync would.
   */
  #setLegRemoval(event, remove) {
    const row = event.currentTarget.closest("[data-row-leg-id]")
    const legId = row?.dataset.rowLegId
    if (!legId || !this.rackingSelection) return

    const assetId = row.dataset.rowAssetId
    const texts = this.legRemovalTextsValue ?? {}
    const history = remove ? (this.legHistoryValue?.[assetId] ?? []) : []
    if (history.length > 0) {
      const why = texts.kept?.[history[0]]
      if (why) {
        this.#setStatus("edit", why.replace("%{leg}", row.dataset.rowLegLabel))
      }
      return
    }

    const result = this.mounted?.racking?.setLegRemoval?.(legId, remove)
    if (result?.refused) {
      const why = texts.kept?.paired
      if (why) {
        this.#setStatus("edit", why.replace("%{leg}", row.dataset.rowLegLabel))
      }
      return
    }
    if (result) this.#refreshRacking()
  }

  #renderAisleLegs(block, aisle) {
    const list = block.querySelector("[data-racking-aisle-legs]")
    if (!list || !this.hasRackingLegTemplateTarget) return

    // A face lists its legs — the assets — never its uprights: the empty ones
    // are drawn hollow on the canvas, and the tally beside the title counts
    // them. An aisle with no legs yet lists none. An upright with a RackEye
    // waiting for a sync is listed too, on a face with an aisle or a new one:
    // it is a leg the next Sync creates, and says so.
    const legs = (aisle.legs ?? []).filter((leg) => leg.assetId || leg.device)
    list.replaceChildren(
      ...legs.map((leg) => {
        const row =
          this.rackingLegTemplateTarget.content.firstElementChild.cloneNode(
            true,
          )

        const name = leg.name || leg.label
        row.querySelector("[data-leg-name]").textContent = name
        // On the row itself, for its buttons: never `data-leg-*`, which names
        // the parts inside it.
        row.dataset.rowLegId = leg.id
        row.dataset.rowAssetId = leg.assetId ?? ""
        row.dataset.rowLegLabel = name

        // The same state words the canvas paints with. A leg with no device
        // carries the dot of its own state: registered, or merely drawn.
        row.querySelector("[data-device-state-dot]").dataset.state = leg.device
          ? leg.device.state
          : leg.assetId
            ? "pending"
            : "planned"

        // What its label says: the code, that it is still waiting for one (the
        // normal state of a registered leg until an installer scans it), or
        // nothing for a leg that is not an asset.
        const code = row.querySelector("[data-leg-scan-code]")
        // Going on the Sync: marked, or past the end the rack was given.
        const going = leg.markedForRemoval || leg.pastEnd
        code.textContent = going
          ? (this.legRemovalTextsValue?.row ?? "")
          : (leg.scanCode ??
            (leg.assetId
              ? code.dataset.awaiting
              : leg.device
                ? code.dataset.created
                : ""))

        // Removing it on the plan, or keeping it — for somebody who may, on
        // a leg facilities has with no unit paired to it.
        // A RackEye in any state but these two has a unit paired to its leg.
        const paired =
          Boolean(leg.device) &&
          !["planned", "pending"].includes(leg.device.state)
        const removable =
          this.canRemoveLegsValue && Boolean(leg.assetId) && !paired
        // Past the end: lengthening the rack keeps it, not these buttons.
        const remove = row.querySelector("[data-leg-remove]")
        if (remove) hideOrShow(remove, !removable || going)
        const keep = row.querySelector("[data-leg-keep]")
        if (keep) {
          hideOrShow(keep, !removable || !leg.markedForRemoval || leg.pastEnd)
        }

        // WHICH unit is on it — the IPUI once installed, the product until then.
        const device = row.querySelector("[data-leg-device]")
        const ipui = this.deviceFactsByPoint?.get(leg.id)?.ipui
        device.textContent = leg.device ? (ipui ?? leg.device.name) : ""
        device.classList.toggle("font-mono", Boolean(ipui))

        this.#setIotLink(
          row.querySelector("[data-leg-link]"),
          this.#legHref(leg.assetId),
        )

        return row
      }),
    )
  }

  #renderDeviceList(devices, placement) {
    if (!this.hasDeviceListTarget || !this.hasDeviceRowTemplateTarget) return

    this.deviceListTarget.replaceChildren(
      ...(devices ?? []).map((device) => this.#deviceRow(device, placement)),
    )

    if (this.hasDeviceEmptyTarget) {
      hideOrShow(this.deviceEmptyTarget, (devices?.length ?? 0) > 0)
    }
  }

  #deviceRow(device, placement) {
    const node =
      this.deviceRowTemplateTarget.content.firstElementChild.cloneNode(true)

    node.querySelector("button").dataset.entityId = device.entityId
    node.querySelector("[data-device-name]").textContent = device.name
    node.querySelector("[data-device-point]").textContent = device.pointLabel
    node.querySelector("[data-device-state-dot]").dataset.state = device.state

    // WHICH AISLE, the other half of the address: a run has two faces, and
    // "Leg 3" alone names one of two legs.
    const aisle = node.querySelector("[data-device-aisle]")
    aisle.textContent =
      placement?.aisles?.[device.side]?.name ||
      device.aisle ||
      aisle.dataset[device.side] ||
      ""

    for (const word of node.querySelectorAll("[data-device-state-word]")) {
      hideOrShow(word, word.dataset.deviceStateWord !== device.state)
    }

    const live = this.deviceFactsByPoint?.get(device.mountingPointId)
    node.querySelector("[data-device-ipui]").textContent = live?.ipui ?? ""
    node.querySelector("[data-device-battery]").textContent =
      formatBattery(live) ?? ""

    this.#setIotLink(
      node.querySelector("[data-device-link]"),
      this.#sensorHref(live?.sensorNodeId),
    )

    return node
  }

  /** Point a link at IoT, or hide it: a link that goes nowhere is worse than none. */
  #setIotLink(link, href) {
    if (!link) return

    hideOrShow(link, href === null)
    if (href !== null) link.href = href
  }

  /**
   * data-action="click->facilities--floorplan-canvas#selectDevice"
   *
   * A device listed on its run's panel. Selecting it opens the device's own
   * panel, which takes the rail — the journey the list exists for.
   */
  selectDevice(event) {
    const entityId = event.currentTarget?.dataset?.entityId
    if (entityId) this.mounted?.properties?.select(entityId)
  }

  // =========================================================================
  // The plan's contents panel
  // =========================================================================

  /**
   * What the plan holds, from the seam, joined with what only the server and
   * this controller know — where facilities files each run, and which physical
   * unit sits on each leg — and handed to the panel as one render-ready state.
   *
   * The same split the device panel makes: the seam reads the canvas and
   * never talks to the server, the panel renders and never looks anything up.
   */
  /**
   * The contents are read only while the panel is OPEN — nothing at connect.
   *
   * Reading them walks every run against the whole scene, and the seam
   * re-reads on every `entity:update` — once per pointer move of a drag, once
   * per telemetry frame. For a panel nobody is looking at, that is all cost.
   * This subscribes when the rail opens it and lets go when it closes, and the
   * subscription answers at once, so an opened panel is never stale.
   */
  #watchInfo(open) {
    if (open && !this.unsubscribeInfo && this.mounted?.planInfo) {
      this.unsubscribeInfo = this.mounted.planInfo.onChange((state) => {
        this.planInfoState = state
        this.#publishInfo()
      })
    } else if (!open && this.unsubscribeInfo) {
      this.unsubscribeInfo()
      this.unsubscribeInfo = null
      this.planInfoState = null
    }
  }

  #publishInfo() {
    const state = this.planInfoState
    if (!state) return
    // A heartbeat or the status fetch asks for a repaint whatever is open;
    // only an open panel takes one.
    if (this.#railTarget("infoPanel")?.hidden !== false) return

    const placements = this.runFacilitiesValue ?? {}
    const row = (device, withFace) => {
      const live = this.deviceFactsByPoint?.get(device.mountingPointId)
      return {
        entityId: device.entityId,
        name: device.name,
        state: device.state,
        side: device.side,
        pointLabel: device.pointLabel,
        withFace,
        ipui: live?.ipui ?? null,
        assetHref: this.#legHref(device.assetId),
        sensorHref: this.#sensorHref(live?.sensorNodeId),
      }
    }

    // BY AISLE, each under its own zone: a zone is filed by aisles, and the
    // two aisles of a rack need not share one. A rack with no aisle on either
    // face is listed on its own, by its uprights — it has no legs.
    const aisles = []
    const racks = []
    for (const run of state.runs) {
      const faces = placements[run.id]?.aisles ?? {}
      const filed = run.aisles.filter((aisle) => faces[aisle.side])
      if (filed.length === 0) {
        racks.push({
          runId: run.id,
          name: run.name,
          uprights: run.uprights,
          devices: run.devices.map((device) => row(device, false)),
        })
        continue
      }

      // A RackEye on a face with no aisle goes under the rack's first aisle,
      // with its face said, so every device is listed once.
      const elsewhere = run.devices.filter((device) => !faces[device.side])
      filed.forEach((aisle, index) => {
        const at = faces[aisle.side]
        aisles.push({
          runId: run.id,
          id: at.id,
          name: at.name,
          zoneId: at.zoneId ?? placements[run.id]?.zoneId ?? null,
          legs: aisle.legs,
          devices: [
            ...run.devices
              .filter((device) => device.side === aisle.side)
              .map((device) => row(device, false)),
            ...(index === 0 ? elsewhere : []).map((device) =>
              row(device, true),
            ),
          ],
        })
      })
    }

    // A barrier's SmartCaps, as a rack's RackEyes: no face to name.
    const barriers = (state.barriers ?? []).map((barrier) => ({
      ...barrier,
      devices: barrier.devices.map((device) => row(device, false)),
    }))

    this.dispatch("state", {
      prefix: "info",
      detail: {
        counts: state.counts,
        unattached: state.unattached,
        aisles,
        racks,
        barriers,
      },
      bubbles: true,
    })
  }

  /** data-action="click->facilities--floorplan-canvas#toggleInfo" */
  toggleInfo() {
    const panel = this.#railTarget("infoPanel")
    const focusWasInside = Boolean(panel?.contains(document.activeElement))

    this.#togglePanel("info")

    // Closed from its own X: focus would fall to <body> with the button it
    // was on. Back to the bar's button, which is what opens it again.
    if (panel?.hidden && focusWasInside && this.hasInfoButtonTarget) {
      this.infoButtonTarget.focus()
    }

    // The zones and their gateways are the server's, and a gateway goes online
    // or a leg gets its label on a phone somewhere else — so the panel asks
    // for them again each time it opens rather than trusting the first paint.
    if (this.#railTarget("infoPanel")?.hidden === false) {
      this.dispatch("opened", { prefix: "info", bubbles: true })
    }
  }

  /**
   * data-action="info:select@document->facilities--floorplan-canvas#selectFromInfo"
   *
   * A rack or a RackEye picked in the contents panel: select it and bring it
   * into view. Selecting a device opens its own panel, which per the rail's
   * rule takes the slot — you find it in the list and you land on it.
   */
  selectFromInfo(event) {
    const entityId = event?.detail?.entityId
    if (!entityId || !this.mounted) return

    // Measured BEFORE the selection takes the rail, off the panel the click
    // came from: every rail panel is one width (`Floorplans::RightRail`), so
    // the one about to open covers exactly what this one does — and the run
    // lands in the part of the canvas left visible beside it.
    //
    // Unless too little is left: on a narrow window the panel covers most of
    // the stage, and framing into a sliver (or a negative width) would put the
    // run off-screen. Then the whole stage is the better answer.
    const panelWidth = this.#railTarget("infoPanel")?.offsetWidth ?? 0
    const stageWidth = this.hasStageTarget ? this.stageTarget.clientWidth : 0
    const covered =
      stageWidth - panelWidth >= MIN_FRAMING_WIDTH_PX ? panelWidth : 0

    this.mounted.properties?.select(entityId)
    this.mounted.editor?.zoomToEntity(entityId, { screenOffsetRight: covered })
  }

  /**
   * Live device state (CONEK-5943).
   *
   * Two sources, one destination. `GET …/device_status` says what everything is
   * doing at the moment the page opened; `FloorplanChannel` says what changes
   * afterwards. Both land in the same seam, which is where the rule that this
   * must never dirty the drawing is enforced.
   *
   * ## Why the fetch waits for the canvas
   *
   * It is called from here — after `mountCanvas` resolved — rather than from a
   * prop on the page, because the canvas hydrates asynchronously. Statuses
   * embedded in the HTML would arrive before there were any devices to apply
   * them to, and every one would be dropped.
   *
   * ## Why the subscription is not optional
   *
   * This subscribes for as long as the editor is open, rather than only while
   * somebody is looking at a panel. The cost is a socket that is silent unless
   * a real device reports; gating it on a panel being open would mean a leg
   * could be struck, go red, and be back to green before anyone opened the
   * panel that was supposed to show it.
   */
  async #wireTelemetry() {
    if (!this.telemetryEnabledValue) return
    if (!this.mounted?.telemetry) return

    await this.#loadDeviceStatus()
    this.#subscribeTelemetry()
  }

  // A STALE READ MUST NOT PAINT OVER A NEWER ONE (CONEK-7732).
  //
  // A reconnect re-reads the snapshot while the channel is already live, so a
  // read and a broadcast can cross: the read holds the state as of when the
  // server answered it, and a broadcast that lands before its response is newer
  // for that leg. Two reconnects close together can also leave two reads out at
  // once, answering in either order. So only the latest read is applied, and
  // never to a leg a broadcast has spoken for since that read started.
  #statusRead = 0
  #spokenSinceRead = new Set()

  async #loadDeviceStatus() {
    if (!this.deviceStatusPathValue) return

    const read = ++this.#statusRead
    this.#spokenSinceRead = new Set()

    try {
      const response = await get(this.deviceStatusPathValue, {
        responseKind: "json",
      })
      if (!response.ok) {
        // Quiet for the same reason as the `catch` below. It runs on every
        // reconnect now, and a deploy's reconnect can meet a pod on its way out.
        console.error(
          "[floorplan-canvas] device status failed to load",
          response.statusCode,
        )
        return
      }

      const body = await response.json
      if (read !== this.#statusRead) return

      const rows = body?.devices ?? []
      const current = rows.filter(
        (row) => !this.#spokenSinceRead.has(row.mountingPointId),
      )

      // WHICH REAL UNIT is fitted where, kept here rather than pushed into the
      // canvas.
      //
      // `DeviceStatusUpdate` — what the engine consumes — deliberately carries
      // only what the canvas PAINTS: a status, a battery, two counts. An IPUI
      // and a node id change nothing on screen, so widening that contract would
      // put facts into the engine for the sole benefit of one panel. They are
      // already in this response, and this panel is the one place that needs
      // them, so this is where they are kept.
      const facts = new Map(
        current.map((row) => [
          row.mountingPointId,
          {
            sensorNodeId: row.sensorNodeId ?? null,
            ipui: row.ipui ?? null,
            serialNumber: row.serialNumber ?? null,
            firmware: row.firmware ?? null,
            lastSeen: row.lastSeen ?? null,
            batteryLevel: row.batteryLevel ?? null,
            batteryMillivolts: row.batteryMillivolts ?? null,
            alertCount: row.alertCount ?? 0,
            warningCount: row.warningCount ?? 0,
            online: row.online ?? null,
            lowBattery: row.lowBattery ?? false,
          },
        ]),
      )
      // A leg a broadcast spoke for keeps what the broadcast merged in.
      for (const point of this.#spokenSinceRead) {
        const known = this.deviceFactsByPoint?.get(point)
        if (known) facts.set(point, known)
      }
      this.deviceFactsByPoint = facts

      // THE LINKS THE SERVER KNOWS AND THIS CANVAS DOES NOT.
      //
      // A leg's asset link is server-owned and merged into the page's content
      // on render — but a plan imported or edited by somebody else since this
      // tab opened has links this canvas has never seen. Every gate that
      // protects a registered leg asks the POINT (delete, remove-bay, resize),
      // so without this they all stay open until a reload.
      this.mounted?.markProjected(
        Object.fromEntries(
          rows
            .filter((row) => row.mountingPointId && row.facilitiesAssetId)
            .map((row) => [row.mountingPointId, row.facilitiesAssetId]),
        ),
      )

      // `this.mounted` is re-checked because this is the far side of an await
      // and a Turbo navigation can have torn the canvas down meanwhile.
      this.mounted?.telemetry?.apply(current)

      // The view mode the plan shows is read again with it — on open, and on
      // every reconnect, when what was said meanwhile is gone.
      this.mounted?.viewModes?.resync()

      // And the device panel, which is open over whichever device is selected
      // and whose every fact about the fitted unit arrived in this response —
      // and the contents panel, which names each unit by the IPUI in it.
      this.#publishDeviceState()
      this.#publishInfo()
      this.#refreshRacking()
    } catch (error) {
      // Deliberately quiet. A plan whose devices are all grey is exactly what
      // an unconfirmed plan looks like anyway, so an error banner here would be
      // indistinguishable from normal — and there is nothing the user can do
      // about it. The console line is for whoever is debugging it.
      console.error("[floorplan-canvas] device status failed to load", error)
    }
  }

  #subscribeTelemetry() {
    // Statically imported, unlike the canvas engine: four other controllers in
    // this app already pull ActionCable into the Stimulus bundle, so deferring
    // it here would split a chunk to save nothing.
    if (!this.element.isConnected || !this.mounted) return

    this.cable = getConsumer(this.spaceGroupIdValue || "default")

    // WHAT WAS SAID WHILE THE SOCKET WAS DOWN IS GONE (CONEK-7732).
    //
    // ActionCable does not replay: a `device_status` broadcast while this tab
    // was reconnecting reaches nobody, and the leg kept the colour it had until
    // somebody reloaded. A socket drops on every deploy, and the one change no
    // device frame announces — a RackEye going offline — is said by the sweep
    // only between minutes 10 and 15 after its 0x75. So every confirmation
    // after the first re-reads the same snapshot the page opened with.
    //
    // The first is skipped because `#wireTelemetry` loaded that snapshot just
    // before subscribing. The flag is this subscription's own rather than the
    // `{ reconnected }` argument ActionCable passes to `connected()` (8.1,
    // `actioncable.esm.js` `confirmation`): the connection sets it for only
    // the FIRST subscription confirmed after a reconnect, so a second channel
    // on this consumer would take it from this one.
    let confirmedBefore = false
    this.telemetryChannel = this.cable.subscriptions.create(
      { channel: "FloorplanChannel", drawing_id: this.drawingIdValue },
      {
        connected: () => {
          if (!confirmedBefore) {
            confirmedBefore = true
            return
          }
          this.#loadDeviceStatus()
        },
        // The channel refuses a tenant without `floorplan.iot_telemetry`, a
        // user without `drawing.read` and a drawing from another tenant. The
        // plan still draws, just without live state, and nothing else on the
        // page would say why.
        rejected: () => {
          console.warn(
            "[floorplan-canvas] FloorplanChannel refused the subscription; live device state is off",
          )
        },
        received: (payload) => {
          // The plan is being captured for a PDF (`exportPdf`): what the
          // channel says waits until it is taken, then is said in order.
          if (this.#heldPushes) {
            this.#heldPushes.push(payload)
            return
          }

          // A device the PROJECTION drew, on a plan this tab already has open
          // (CONEK-7243).
          //
          // Handled before the `device_status` filter because it is the thing
          // that makes the status usable: an installer pairs a leg, the server
          // fits a `smart_device` to it, and a status naming that mounting
          // point is dropped here until the device exists in this scene. The
          // plan sat bare through the pairing, through a critical event and
          // through the broadcast that reported it.
          //
          // The VERSION goes with it. The server wrote the entity with
          // `update_columns` and bumped `content_version` behind this editor's
          // back, so without adopting it the next delta save leaves with a
          // stale `base_version` and is refused — the same out-of-band bump
          // the subsets API makes, adopted through the same seam.
          //
          // It returns here rather than falling through: the colour arrives in
          // its own `device_status` message a moment later, on the road it
          // already travelled. This one only has to put something on the plan
          // for that colour to land on.
          // Another tab, or an open somewhere, synced this plan from
          // facilities (`Floorplans::SyncFromFacilities`). A tab with nothing
          // unsaved simply reloads onto the new plan; one with unsaved work is
          // told, because its next save will be refused as stale.
          if (payload?.action === "plan_synced") {
            this.#planSynced(payload.content_version)
            return
          }

          if (payload?.action === "entity_added") {
            this.mounted?.adoptEntity(payload.entity)
            if (typeof payload.content_version === "number") {
              this.mounted?.subsets.adoptContentVersion(payload.content_version)
            }
            return
          }

          if (payload?.action !== "device_status") return

          // Newer than any read still out — see `#spokenSinceRead`.
          if (payload.mounting_point_id) {
            this.#spokenSinceRead.add(payload.mounting_point_id)
          }

          // The point is an asset, which THIS editor may not know yet — the
          // same drift `#loadDeviceStatus` closes on open, for a plan somebody
          // else changed while this tab was already sitting there.
          //
          // Called on every frame, including the hundred heartbeats a busy
          // warehouse produces in an hour: `markProjected` is one pass over the
          // scene and returns 0 when the point already holds the link, so the
          // repeat costs a walk and changes nothing.
          if (payload.mounting_point_id && payload.facilities_asset_id) {
            this.mounted?.markProjected({
              [payload.mounting_point_id]: payload.facilities_asset_id,
            })
          }

          // One update, through the same door the initial batch used. The
          // server sends the FOLDED status rather than the raw event, so
          // nothing here has to know what an impact means.
          this.mounted?.telemetry?.apply([
            {
              mountingPointId: payload.mounting_point_id,
              status: payload.status,
              // Only when the message carries one. A telemetry frame has no
              // `sold_at` key at all, and passing `null` for it would unstamp
              // every device the next time a sensor spoke — so the absent key
              // has to stay absent, not become a null.
              ...("sold_at" in payload ? { soldAt: payload.sold_at } : {}),
              ...("paired" in payload ? { paired: payload.paired } : {}),
              ...("review" in payload ? { review: payload.review } : {}),
              // Painted too: Live's purple dot (CONEK-8872). Absent on a
              // message that says nothing about the battery.
              ...("low_battery" in payload
                ? { lowBattery: payload.low_battery === true }
                : {}),
              // Not painted: the canvas colours a battery from the server's
              // `tone`. The panel's figure, millivolts included, is the
              // #mergeDeviceFacts call below.
              batteryLevel: payload.battery_level,
              alertCount: payload.alert_count,
              warningCount: payload.warning_count,
              eventType: payload.event_type,
              severity: payload.severity,
              occurredAt: payload.occurred_at,
            },
          ])

          // THE SAME FRAME, kept for the panel that reads rather than paints.
          //
          // `telemetry.apply` hands the canvas what it draws with and stops
          // there, by design. The device panel shows the battery and when the
          // unit last spoke, and those live in the map this controller holds —
          // which, without this, would be filled once by the initial fetch and
          // never move again.
          // And the view mode, which may read what the frame moved — a
          // battery, a temperature, a leg struck in a range reaching today.
          this.mounted?.viewModes?.heard({
            mountingPointId: payload.mounting_point_id,
            eventType: payload.event_type,
            severity: payload.severity,
          })

          this.#mergeDeviceFacts(payload.mounting_point_id, {
            batteryLevel: payload.battery_level,
            batteryMillivolts: payload.battery_millivolts,
            online: payload.online,
            lowBattery: payload.low_battery,
            lastSeen: payload.last_seen,
            alertCount: payload.alert_count,
            warningCount: payload.warning_count,
            // WHICH UNIT it is, not only what it is doing. The frame that
            // follows an installation is the first time this leg has an IPUI
            // and a node id at all. Absent keys are dropped by the merge, so a
            // broadcaster that does not send them cannot blank what is known.
            sensorNodeId: payload.sensor_node_id,
            ipui: payload.ipui,
            serialNumber: payload.serial_number,
            firmware: payload.firmware,
          })
        },
      },
    )
  }

  #planSynced(version) {
    if (typeof version === "number" && version <= this.contentVersionValue) {
      return
    }
    // Not now — a change of ours is out, or one is being asked in a dialog
    // whose words describe the plan as it is: remembered, and fetched once
    // that settles (`#catchUp`). Dropped, the editor stayed a version behind
    // and its next save was refused as stale.
    if (this.#busy || this.#askingPlanChange()) {
      // A broadcast without its version is taken as newer than anything.
      const announced = typeof version === "number" ? version : Infinity
      this.#missedVersion = Math.max(this.#missedVersion ?? 0, announced)
      return
    }

    if (!this.saveState || this.saveState === "saved") {
      this.#refreshPlan()
      return
    }

    this.#setStatus("edit", this.planSyncedMessageValue)
  }

  /** Its own method so a test can stand in for the reload. */
  reloadPlan() {
    window.location.reload()
  }

  /** data-action="click->facilities--floorplan-canvas#requestSave" */
  requestSave() {
    this.mounted?.save.requestSave()
  }

  #renderSaveStatus(status) {
    // The engine's own word, kept for when editing starts or stops: it is not
    // said again until the next change.
    this.#saveStatus = status
    // Somebody who is only looking has nothing to save. A canvas that turns
    // dirty under them — an edit the engine let through locally, a content
    // migration on load — is never "unsaved": no Save that does nothing, and
    // the plan other editors change is always applied (`#planSynced`).
    if (this.#locked) status = { state: "saved" }
    if (this.hasSaveIndicatorTarget) {
      this.saveIndicatorTarget.hidden = this.#locked
    }
    this.saveState = status.state
    if (status.state === "saved") this.#renderSyncBadge()
    if (status.state === "saved" || status.state === "error") {
      this.#faceSavePending = false
      const waiting = this.#saveWaiters
      this.#saveWaiters = []
      for (const resolve of waiting) resolve(status.state === "saved")
    }

    // A refusal because the plan moved on is the one save error with a way
    // out, and the raw message does not say it: reload.
    const message =
      status.code === "stale"
        ? this.staleSaveMessageValue
        : (status.message ?? "")
    this.#setStatus("save", status.state === "error" ? message : "")

    if (!this.hasSaveIndicatorTarget) return

    // All four states are rendered by SaveIndicatorComponent and all but one
    // are hidden. Choosing between them is the whole job here — no icon name,
    // no colour and no translated string lives in this file, which is the
    // point: those belong to the component and to the ten locale files.
    const states =
      this.saveIndicatorTarget.querySelectorAll("[data-save-state]")
    for (const el of states) {
      el.hidden = el.dataset.saveState !== status.state
    }
  }

  /** data-action="click->facilities--floorplan-canvas#selectTool" */
  selectTool(event) {
    const tool = event.params?.tool ?? event.currentTarget?.dataset?.tool
    if (!tool || !this.mounted) return
    // Looking, the bar offers Select and Pan alone, and so does this.
    if (this.#locked && !VIEW_TOOLS.includes(tool)) return

    // Ask, and let the subscription answer. Painting the active state here
    // instead is what made the toolbar disagree with the canvas.
    this.mounted.editor.setTool(tool)
  }

  /** data-action="click->facilities--floorplan-canvas#armBarrier" */
  armBarrier(event) {
    if (this.#locked) return
    const code = event.currentTarget?.dataset?.barrierCode
    if (!code || !this.mounted?.barriers) return

    // Asked of the canvas, which knows whether the barrier may be drawn; the
    // active state follows from the tool change, as for every other tool.
    // Which barrier is remembered first: one Barrier tool draws them all, so
    // the tool change alone cannot say which card to light.
    const previous = this.#armedBarrier
    this.#armedBarrier = code
    const state = this.mounted.barriers.arm(code)
    if (state !== "offered") {
      this.#armedBarrier = previous
      this.#toastBarrier(this.barrierUnavailableTextsValue?.[state] ?? "")
      return
    }
    // From one barrier to another the tool does not change, so no tool change
    // arrives to move the light from one card to the other.
    this.#renderActiveTool("barrier")
  }

  /** The gen4 barrier the Barrier tool was last armed with. */
  #armedBarrier = null

  /**
   * Why a barrier cannot be drawn, as a toast — as /drawings says it — cloned
   * from the server-rendered `Ui::ToastComponent` and given the reason. One at
   * a time: a second click that fails the same way replaces the first rather
   * than stacking. Without the toast container (or the template), the status
   * line says it instead.
   */
  #toastBarrier(reason) {
    if (!reason) return
    const container = document.getElementById("toast-container")
    const template = this.hasBarrierToastTarget ? this.barrierToastTarget : null
    if (!container || !template) {
      this.#setStatus("edit", reason)
      return
    }

    const toast = template.content.firstElementChild.cloneNode(true)
    const slot = [...toast.querySelectorAll("*")].find(
      (el) =>
        el.childElementCount === 0 &&
        el.textContent.trim() === BARRIER_TOAST_REASON,
    )
    if (slot) slot.textContent = reason
    document.getElementById(BARRIER_TOAST_ID)?.remove()
    toast.id = BARRIER_TOAST_ID
    container.appendChild(toast)
  }

  /**
   * data-action="click->facilities--floorplan-canvas#exportPdf"
   *
   * The plan as it is shown — in its view, with its legend — as a PDF
   * (`floorplan-pdf-export.ts`, laid out by `Floorplans::ExportPdf`), shown
   * in the preview before it is saved. One at a time; a view still loading, or
   * one that failed to load, is not printed, and the status line says which.
   */
  async exportPdf(event) {
    event?.preventDefault()
    const pdf = this.mounted?.pdfExport
    if (!pdf || pdf.busy) return

    const texts = this.pdfTextsValue ?? {}
    this.#markPdfBusy(true)
    this.#setStatus("export", texts.exporting ?? "")
    try {
      const result = await pdf.export()
      if (result.ok) {
        this.#setStatus("export", "")
        this.#openPdfPreview(result.blob, result.filename)
      } else if (result.reason !== "busy") {
        // `not_ready`, `view_failed` or `failed`: each has its own text.
        this.#setStatus("export", texts[result.reason] ?? texts.failed ?? "")
      }
    } finally {
      this.#markPdfBusy(false)
    }
  }

  /**
   * Show the PDF in the preview (`Floorplans::PdfPreviewComponent`): the
   * viewer is cloned in with the file's URL, and opened. With no preview on
   * the page the file is simply saved.
   */
  #openPdfPreview(blob, filename) {
    const dialog = document.getElementById(PDF_PREVIEW_ID)
    if (
      !dialog ||
      !this.hasPdfPreviewTarget ||
      !this.hasPdfViewerTemplateTarget
    ) {
      saveFile(blob, filename)
      return
    }

    this.#closePdf()
    const url = URL.createObjectURL(blob)
    this.#pdf = { blob, filename, url }
    const viewer =
      this.pdfViewerTemplateTarget.content.firstElementChild.cloneNode(true)
    viewer.setAttribute("data-pdf-viewer-document-url-value", url)
    viewer.setAttribute("data-pdf-viewer-document-name-value", filename)
    this.pdfPreviewTarget.replaceChildren(viewer)
    this.#showDialog(dialog)
  }

  /** data-action="click->facilities--floorplan-canvas#downloadPdf" */
  downloadPdf(event) {
    event?.preventDefault()
    if (this.#pdf) saveFile(this.#pdf.blob, this.#pdf.filename)
  }

  /** data-action="modal:close->facilities--floorplan-canvas#pdfPreviewClosed" */
  pdfPreviewClosed() {
    this.#closePdf()
  }

  /** The viewer out of the preview — which disconnects it — and the file let go. */
  #closePdf() {
    if (this.hasPdfPreviewTarget) this.pdfPreviewTarget.replaceChildren()
    if (this.#pdf) URL.revokeObjectURL(this.#pdf.url)
    this.#pdf = null
  }

  // The PDF the preview shows — its file, name and object URL — or null.
  #pdf = null

  /** The PDF's post: the capture, the view and its legend, as JSON. */
  async #postPdf(body) {
    const response = await post(this.pdfExportPathValue, {
      body,
      contentType: "application/json",
      // Not one request.js knows: it then accepts anything, the PDF included.
      responseKind: "pdf",
    })
    return response.response
  }

  /**
   * What the plan holds, as the info panel's Summary counts it
   * (`floorplans--info#paintCounts`): its counts, and its aisles — the faces
   * facilities files, as `#publishInfo` lists them. Null before the plan is up.
   */
  #planContents() {
    const state = this.mounted?.planInfo?.read?.()
    if (!state) return null

    const placements = this.runFacilitiesValue ?? {}
    const aisles = state.runs.reduce((total, run) => {
      const faces = placements[run.id]?.aisles ?? {}
      return total + run.aisles.filter((aisle) => faces[aisle.side]).length
    }, 0)
    return { ...state.counts, aisles }
  }

  /** Hold what the channel says until the release is called (`exportPdf`). */
  #holdPushes() {
    this.#heldPushes = []
    return () => {
      const held = this.#heldPushes ?? []
      this.#heldPushes = null
      for (const payload of held) this.telemetryChannel?.received?.(payload)
    }
  }

  // What the channel said while the plan was being captured, or null.
  #heldPushes = null

  /** The button says it is working — the loading exception to "never disabled". */
  #markPdfBusy(busy) {
    if (!this.hasPdfButtonTarget) return
    if (busy) this.pdfButtonTarget.setAttribute("aria-busy", "true")
    else this.pdfButtonTarget.removeAttribute("aria-busy")
  }

  /** data-action="click->facilities--floorplan-canvas#exportAs" */
  exportAs(event) {
    const format = event.currentTarget?.dataset?.exportFormat
    if (format) this.mounted?.editor.exportAs(format)
  }

  /** data-action="click->facilities--floorplan-canvas#setMeasurementSystem" */
  setMeasurementSystem(event) {
    const system = event.currentTarget?.dataset?.measurementSystem
    if (system) this.mounted?.editor.setMeasurementSystem(system)
  }

  /** data-action="click->facilities--floorplan-canvas#undo" */
  undo() {
    if (this.#locked) return
    this.mounted?.editor.undo()
  }

  /** data-action="click->facilities--floorplan-canvas#redo" */
  redo() {
    if (this.#locked) return
    this.mounted?.editor.redo()
  }

  #renderActiveTool(tool) {
    // `data-active` is the design system's contract, so no DS class names leak
    // into JavaScript (Ui::ToolbarButtonComponent `active:`).
    for (const button of this.element.querySelectorAll("[data-tool]")) {
      this.#setActive(button, button.dataset.tool === tool)
    }

    // A group trigger lights when the canvas is on any of its members.
    for (const trigger of this.element.querySelectorAll("[data-tool-group]")) {
      const members = trigger.dataset.toolGroup.split(" ")
      this.#setActive(trigger, members.includes(tool))
    }

    // The Products tab's barrier cards: the one the Barrier tool is drawing.
    for (const card of this.element.querySelectorAll("[data-barrier-code]")) {
      this.#setActive(
        card,
        tool === "barrier" && card.dataset.barrierCode === this.#armedBarrier,
      )
    }
  }

  // An item that may not be drawn is disabled and says why — the reason is a
  // data attribute the component renders, so no class name lives here.
  #paintBarrierOffers(offers) {
    for (const item of this.element.querySelectorAll("[data-barrier-code]")) {
      const offer = offers.find(
        (candidate) => candidate.code === item.dataset.barrierCode,
      )
      const state = offer?.state ?? "missing"
      // One that failed to load stays clickable: the click asks for it again.
      const disabled = state !== "offered" && state !== "error"
      item.disabled = disabled
      item.toggleAttribute("data-disabled", disabled)
      item.setAttribute("aria-disabled", String(disabled))
      const reason =
        state === "offered"
          ? ""
          : (this.barrierUnavailableTextsValue?.[state] ?? "")
      if (disabled) {
        item.dataset.reason = reason
      } else {
        delete item.dataset.reason
      }
      // Said on the card itself, under its code (`ProductsPanelComponent`).
      const line = item.querySelector("[data-barrier-reason]")
      if (line) line.textContent = reason
    }
  }

  #setActive(el, active) {
    el.dataset.active = String(active)
    if (el.hasAttribute("aria-pressed")) {
      el.setAttribute("aria-pressed", String(active))
    }
  }

  /** data-action="click->…#clearSelection" on the panel's close button */
  clearSelection() {
    this.mounted?.properties.clearSelection()
  }

  #renderProperties({ body }) {
    if (!this.hasPropertiesPanelTarget) return

    const bodies =
      this.propertiesPanelTarget.querySelectorAll("[data-panel-body]")
    let shown = null

    for (const el of bodies) {
      const matches = el.dataset.panelBody === body
      el.hidden = !matches
      if (matches) shown = el
    }

    // No body for the shown selection (e.g. a racking run, which has an aside
    // of its own) closes this one — same as no selection at all. Through the
    // rail, which is the only writer of any right-hand panel's `hidden`.
    this.#selectPanel("properties", shown !== null)

    if (shown && this.hasPropertiesTitleTarget) {
      this.propertiesTitleTarget.textContent = shown.dataset.panelTitle ?? ""
    }

    // No id in the header. The entity's uuid means nothing to anybody reading
    // the plan — `#c16c42` read as a colour. A device's header is its IPUI,
    // and `#publishDeviceState` alone writes it: the two selection seams fire
    // in whichever order they were subscribed, so this must not clear it.
    if (this.hasPropertiesIdTarget && body !== "device") {
      this.propertiesIdTarget.textContent = ""
    }

    if (shown) this.#fillProperties(shown)
  }

  #fillProperties(body) {
    const values = this.mounted?.properties.read()
    if (!values) return

    for (const el of body.querySelectorAll("[data-field]")) {
      const value = values[el.dataset.field]
      if (value === undefined) continue

      this.#fillControl(el, value)
    }

    for (const el of body.querySelectorAll("[data-only-for]")) {
      const absent = values[el.dataset.onlyFor] === undefined
      hideOrShow(el, absent)

      const panel = el.closest("[data-ui--tabs-target='panel']")
      const tabId = panel?.getAttribute("aria-labelledby")
      const tab = tabId ? body.querySelector(`#${CSS.escape(tabId)}`) : null
      if (tab) hideOrShow(tab, absent)
    }

    for (const group of body.querySelectorAll("[data-when]")) {
      const value = values[group.dataset.when]
      for (const option of group.querySelectorAll("[data-when-value]")) {
        hideOrShow(option, option.dataset.whenValue !== String(value))
      }
    }

    for (const el of body.querySelectorAll("[data-reading]")) {
      const reading = values[el.dataset.reading]
      el.textContent = reading === undefined ? "—" : String(reading)
    }
  }

  #fillControl(el, value) {
    const picker = el.querySelector("[data-ui--color-field-target='picker']")
    if (picker) {
      const hex = String(value)
      picker.value = hex
      el.querySelector("[data-ui--color-field-target='hex']").value =
        hex.toUpperCase()
      el.querySelector(
        "[data-ui--color-field-target='swatch']",
      ).style.backgroundColor = hex
      return
    }

    if (el.matches("input")) {
      el.value = String(value)
    } else {
      const input = el.querySelector("input[type='number'], input[type='text']")
      if (input) input.value = String(value)
    }

    for (const option of el.querySelectorAll("[data-value]")) {
      const active = option.dataset.value === String(value)
      option.dataset.active = String(active)
      if (option.hasAttribute("aria-checked")) {
        option.setAttribute("aria-checked", String(active))
      }
    }
  }

  /** data-action="numeric-field:commit->…#commitField" and change-> for colours */
  commitField(event) {
    const el = event.target.closest("[data-field]")
    if (!el) return

    const value = event.detail?.value ?? event.target.value
    this.mounted?.properties.set(el.dataset.field, value)
  }

  /** data-action="click->…#commitPreset" on a quick-pick row */
  commitPreset(event) {
    const preset = event.target.closest("[data-value]")
    const group = event.target.closest("[data-field]")
    if (!preset || !group) return

    if (
      this.mounted?.properties.set(
        group.dataset.field,
        Number(preset.dataset.value),
      )
    ) {
      this.#fillProperties(group.closest("[data-panel-body]"))
    }
  }

  /** data-action="click->…#commitChoice" on a segmented toggle */
  commitChoice(event) {
    const option = event.target.closest("[data-value]")
    const group = event.target.closest("[data-field]")
    if (!option || !group) return

    if (
      this.mounted?.properties.set(group.dataset.field, option.dataset.value)
    ) {
      this.#fillProperties(group.closest("[data-panel-body]"))
    }
  }

  #renderHistory({ canUndo, canRedo }) {
    if (this.hasUndoButtonTarget) this.undoButtonTarget.disabled = !canUndo
    if (this.hasRedoButtonTarget) this.redoButtonTarget.disabled = !canRedo
  }

  /**
   * data-action="turbo:before-visit@document->facilities--floorplan-canvas#confirmLeave"
   *
   * Leaving the editor through a Turbo visit — the footer's site and building,
   * a link in a panel, the search palette, the account menu.
   *
   * The plan saves on a debounce, so the screen can be ahead of the database,
   * and following a link then loses whatever had not landed. It ASKS rather
   * than saving on somebody's behalf: a write nobody requested, triggered by a
   * navigation, is the move this editor refuses everywhere else.
   *
   * Only once the canvas has reported a save state. Before that there is
   * nothing on it anybody could have changed.
   */
  confirmLeave(event) {
    if (this.#locked) return
    if (!this.mounted || !this.saveState || this.saveState === "saved") return

    if (!window.confirm(this.leaveUnsavedMessageValue)) event.preventDefault()
  }

  /**
   * data-action="click->facilities--floorplan-canvas#syncFacilities"
   *
   * Bring the plan in step with its floor's facilities, and create there what
   * it has waiting (`Floorplans::RegisterInFacilities`). The server writes the
   * plan and answers with it, applied where the editor stands (`#adopt`).
   * Asked first, in the design system's dialog, with what will be created.
   *
   * One at a time: a second click while the list loads is dropped, and a
   * change of the server's still out (a face, the plan fetched after one) is
   * waited for — the list must read the plan it leaves.
   */
  async syncFacilities(event) {
    event?.preventDefault()
    if (this.#locked) return
    if (!this.hasSyncPathValue || this.#syncOpening) return
    // A face change being asked about is answered first: the list would read
    // a plan that change is about to alter.
    if (this.#pendingFaceChange) return
    const dialog = document.getElementById(SYNC_DIALOG_ID)
    if (!dialog || dialog.open) return

    this.#syncOpening = true
    try {
      while (this.#busy) await this.#whenIdle()

      // The plan the Sync reads is the SAVED one, and RackEyes just put on it
      // are in the save the editor makes on its own a moment later: made now
      // and waited for — but not forever: a save that never reports says so.
      if (this.mounted && this.saveState && this.saveState !== "saved") {
        this.mounted.save?.requestSave?.()
        if (!(await this.#afterSave(SYNC_SAVE_WAIT_MS))) {
          this.#setStatus("edit", this.syncUnsavedMessageValue)
          return
        }
      }

      this.syncDigest = null
      if (!(await this.#loadSyncPreview(dialog))) return
      // Gone meanwhile (another page): there is no editor to show it over.
      if (!this.element.isConnected || !dialog.isConnected) return

      this.#showDialog(dialog)
    } finally {
      this.#syncOpening = false
    }
  }

  // The Sync's list being fetched: a second click waits for nothing.
  #syncOpening = false

  /**
   * What the Sync will create in facilities, rendered by the server into the
   * dialog. Remembers the digest it was shown with — a registration of
   * anything else is refused — and hides Confirm while something stops it.
   * False when it could not be read; the Sync is then not offered.
   */
  async #loadSyncPreview(dialog) {
    const slot = dialog.querySelector("[data-sync-preview-slot]")
    if (!slot || !this.hasSyncPreviewPathValue) return true

    try {
      const response = await get(this.syncPreviewPathValue, {
        responseKind: "html",
      })
      if (!response.ok) throw new Error(String(response.statusCode))
      slot.innerHTML = await response.text
    } catch {
      this.#setStatus("edit", this.loadErrorMessageValue)
      return false
    }

    const preview = slot.querySelector("[data-sync-preview]")
    const empty = preview?.dataset.empty !== "false"
    const registrable = preview?.dataset.registrable === "true"
    // What stops the creating does not stop the Sync: it posts no digest, so
    // it only brings the plan in step with facilities, as the list says.
    // Only a sync that cannot run at all is not offered.
    const blocked = preview?.dataset.blocked === "true"
    const pullable = preview?.dataset.pullable !== "false"
    this.syncDigest =
      empty || blocked || !registrable
        ? null
        : (preview?.dataset.digest ?? null)
    const confirm = dialog.querySelector("[data-sync-dialog-confirm]")
    if (confirm) hideOrShow(confirm, !pullable)
    return true
  }

  /**
   * How many devices wait for a Sync — `planned`: a RackEye on an upright
   * with no leg, a SmartCap on a post with no asset. Read once per save
   * rather than per change: an edit only lasts until it is saved, and the
   * contents are a walk of the whole plan.
   */
  #renderSyncBadge() {
    if (!this.hasSyncBadgeTarget) return
    const info = this.mounted?.planInfo?.read?.()
    const barriers = info?.barriers ?? []
    const planned = [...(info?.runs ?? []), ...barriers]
      .flatMap((host) => host.devices ?? [])
      .filter((device) => device.state === "planned").length
    // A registered post a SmartCap left goes with the Sync (CONEK-8441), as a
    // leg removed on the plan does.
    const barePosts = barriers.reduce(
      (sum, barrier) =>
        sum +
        Math.max(
          0,
          (barrier.registeredPosts ?? 0) -
            (barrier.devices ?? []).filter((device) => device.assetId).length,
        ),
      0,
    )
    // Every change waiting for it: the devices, the aisles that grow to
    // share a rack (`#uprightGrowths`), the legs removed on the plan and the
    // posts a SmartCap left.
    const removals = this.mounted?.racking?.markedForRemovalCount?.() ?? 0
    const pending =
      planned + this.#uprightGrowths().length + removals + barePosts
    const before = this.syncPending
    this.syncPending = pending
    this.syncBadgeTarget.textContent = String(pending)
    hideOrShow(this.syncBadgeTarget, pending === 0)
    // A count that changed pops, as a cart's does — up when a change starts
    // waiting, down when one stops (undone, removed) — never on the first
    // paint, and never when it did not change: the status fetch and a
    // heartbeat repaint this too. At none it is hidden, so nothing pops.
    if (before !== undefined && pending > 0 && pending !== before) {
      this.#popSyncBadge()
    }
  }

  #popSyncBadge() {
    if (prefersReducedMotion() || !this.syncBadgeTarget.animate) return

    this.syncBadgeTarget.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.35)" },
        { transform: "scale(1)" },
      ],
      { duration: 320, easing: "ease-out" },
    )
  }

  /**
   * The aisles whose count of uprights changes on the Sync: to the length a
   * person gave their rack, or — sharing a rack with the aisle on its other
   * face (`RegistrationPlan#align_shared_racks`) — the shorter to the
   * longer's count. Each as the aisle, from and to.
   */
  #uprightGrowths() {
    const counts = this.aisleUprightsValue ?? {}
    const planned = this.mounted?.racking?.plannedUprights?.() ?? {}
    const byRun = new Map()
    for (const [id, at] of Object.entries(this.aisleFacesValue ?? {})) {
      if (typeof counts[id] !== "number") continue
      byRun.set(at.runId, [...(byRun.get(at.runId) ?? []), id])
    }
    const growths = []
    for (const [runId, ids] of byRun.entries()) {
      // A length a person gave the rack is what every aisle on it takes.
      const given = planned[runId]
      if (typeof given === "number") {
        for (const id of ids) {
          if (counts[id] !== given)
            growths.push({ id, from: counts[id], to: given })
        }
        continue
      }
      if (ids.length < 2) continue
      const to = Math.max(...ids.map((id) => counts[id]))
      for (const id of ids) {
        if (counts[id] < to) growths.push({ id, from: counts[id], to })
      }
    }
    return growths
  }

  /** What the aisle on this face of the selected rack grows to on the Sync, or null. */
  #uprightGrowth(placement, side) {
    const id = placement?.aisles?.[side]?.id
    if (!id) return null
    return this.#uprightGrowths().find((growth) => growth.id === id) ?? null
  }

  /** data-action="click->facilities--floorplan-canvas#confirmSync" */
  async confirmSync() {
    document.getElementById(SYNC_DIALOG_ID)?.close()
    // Edited while the list was loading: the list may no longer be what this
    // plan would create, and the answer could not be applied over the edit.
    // Saved, and the list shown again.
    if (this.mounted && this.saveState && this.saveState !== "saved") {
      void this.syncFacilities()
      return
    }
    const body = this.syncDigest ? { digest: this.syncDigest } : {}
    const outcome = await this.#serverOp(this.syncPathValue, body)
    // What was shown is no longer what would be created: shown again, once
    // the plan the server just synced is fetched (`syncFacilities` waits).
    if (outcome?.status === 409) void this.syncFacilities()
  }

  /** data-action="close->facilities--floorplan-canvas#syncDialogClosed" */
  syncDialogClosed() {
    this.#catchUp()
  }

  // ── Faces of a rack ───────────────────────────────────────────────────────
  //
  // Which aisle stands on a face is the server's to change (`BindFace`): it
  // moves the aisle's legs and RackEyes — swapping it with the aisle already
  // on the face — and answers with the plan, applied in place. So like Sync
  // it is refused with unsaved work and asked first, in a dialog that says
  // exactly what will move and which counts of uprights will grow. Enabling or
  // disabling a face with no aisle is an edit to the drawing, saved like any
  // other.

  /** data-action="change->facilities--floorplan-canvas#bindFace" on a face's select. */
  bindFace(event) {
    if (this.#locked) return
    const select = event.target
    const side = select.dataset.rackingFaceSelect
    const selection = this.rackingSelection
    if (!selection || !side) return

    const placement = selection.placement
    const current = placement?.aisles?.[side] ?? null
    const chosen = select.value
    if (chosen === (current?.id ?? "")) return

    const revert = () => setSelectValue(select, current?.id ?? "")
    // "No aisle" moves nothing anywhere; splitting off is the button's job.
    // Nor while the Sync's list loads: that list reads the plan as it is.
    if (chosen === "" || this.#busy || this.#syncOpening) return revert()
    // The switch that just enabled this face, still on its way: asked again
    // once it lands.
    if (this.#faceSavePending && this.saveState !== "saved") {
      void this.#afterSave().then((saved) => {
        if (saved && select.value === chosen) this.bindFace(event)
        else revert()
      })
      return
    }
    if (!this.#nothingUnsaved()) return revert()

    const from = this.aisleFacesValue?.[chosen] ?? null
    // RackEyes waiting for a sync on a face an aisle leaves stay on their
    // uprights: the chosen one leaves its face, the one here leaves this one.
    const leaving = [from, current && { runId: selection.runId, side }]
    const stay = leaving.some((face) => face && this.#pendingOn(face))
    const faces = this.#facesOn()
    const within = from?.runId === selection.runId
    const kind = current ? "swap" : from ? "move" : "bind"
    // What the dialog says happens, which depends on where the chosen aisle
    // stands now: on this rack's other face, on another rack, or nowhere.
    const message = current
      ? within
        ? "swap_within"
        : from
          ? "swap"
          : "swap_out"
      : from
        ? within
          ? "move_within"
          : "move"
        : "bind"
    // The counts that grow wait for the Sync: said, not refused.
    const grows = this.#growingUprights(
      selection.runId,
      side,
      chosen,
      current,
      from,
    )
    // A growth nobody here may make is refused by the server; said now, rather
    // than offered in a dialog whose Confirm then fails.
    if (grows.length > 0 && !this.canGrowAislesValue) {
      this.#setStatus("edit", this.faceGrowRefusedMessageValue)
      return revert()
    }

    const name = this.#aisleName(chosen)
    // One row per aisle the change moves or grows, as the Sync lists what it
    // creates: the chosen one onto this face; the one it displaces to where
    // the chosen one stood — the other face of this rack, the other rack, or
    // a rack of its own; and any aisle staying put whose uprights grow.
    const texts = this.faceDialogTextsValue ?? {}
    const growthOf = (id) => grows.find((grow) => grow.id === id) ?? null
    const rows = [
      this.#aisleRow(chosen, texts[`badge_${side}`], {
        primary: true,
        grow: growthOf(chosen),
      }),
    ]
    if (current) {
      const lands = within
        ? texts[`badge_${from.side}`]
        : from
          ? texts.badge_swapped
          : texts.badge_own_rack
      rows.push(
        this.#aisleRow(current.id, lands, { grow: growthOf(current.id) }),
      )
    }
    for (const grow of grows) {
      if (grow.id === chosen || grow.id === current?.id) continue
      rows.push(
        this.#aisleRow(grow.id, texts.badge_stays, { grow, carried: false }),
      )
    }

    const notes = []
    if (stay) notes.push(["pending_stay", {}])
    if (
      this.#reshapedRacks(selection.runId, side, chosen, current, from).some(
        (rack) => this.#dropsLength(rack),
      )
    ) {
      notes.push(["length_dropped", {}])
    }
    // Moved off a rack with nothing on its other face, the rack goes with it
    // (`SyncFromFacilities` drops a run no aisle stands on) — unless RackEyes
    // pending Sync stand on it, on either face: then it stays, drawn here.
    const otherFace = from && {
      runId: from.runId,
      side: oppositeFace(from.side),
    }
    if (
      kind === "move" &&
      !within &&
      !faces.get(`${otherFace.runId}:${otherFace.side}`) &&
      !this.#pendingOn(from) &&
      !this.#pendingOn(otherFace)
    ) {
      notes.push(["rack_removed", { aisle: name }])
    }

    this.#openFaceDialog(
      kind,
      message,
      {
        aisle: name,
        displaced: current ? this.#aisleName(current.id) || current.name : "",
      },
      rows,
      notes,
      { run_id: selection.runId, side, asset_group_id: chosen },
      revert,
    )
  }

  /** data-action="click->facilities--floorplan-canvas#separateFace" */
  separateFace(event) {
    if (this.#locked) return
    event?.preventDefault()
    const side = event.target.closest("[data-racking-face-separate]")?.dataset
      .rackingFaceSeparate
    const selection = this.rackingSelection
    const filed = side && selection?.placement?.aisles?.[side]
    if (!filed || this.#busy || this.#syncOpening || !this.#nothingUnsaved())
      return
    // RackEyes waiting for a sync on the face stay on its uprights.
    const notes = this.#pendingOn({ runId: selection.runId, side })
      ? [["pending_stay", {}]]
      : []
    const texts = this.faceDialogTextsValue ?? {}
    // The aisle staying on the rack grows on the Sync to every RackEye
    // waiting on it, the leaving face's included (`#heldGrowth`) — refused
    // now to somebody who may not, rather than when Confirm fails.
    const staying = selection.placement?.aisles?.[oppositeFace(side)]
    const grows = staying
      ? this.#heldGrowth(selection.runId, [staying.id], [side])
      : []
    if (grows.length > 0 && !this.canGrowAislesValue) {
      this.#setStatus("edit", this.faceGrowRefusedMessageValue)
      return
    }
    if (
      staying &&
      this.#dropsLength({
        runId: selection.runId,
        aisleIds: [staying.id],
        leaving: [side],
      })
    ) {
      notes.push(["length_dropped", {}])
    }

    this.#openFaceDialog(
      "separate",
      "separate",
      { aisle: this.#aisleName(filed.id) || filed.name, displaced: "" },
      [
        this.#aisleRow(filed.id, texts.badge_own_rack, { primary: true }),
        ...grows.map((grow) =>
          this.#aisleRow(grow.id, texts.badge_stays, { grow, carried: false }),
        ),
      ],
      notes,
      { run_id: selection.runId, side, separate: "1" },
      null,
    )
  }

  /**
   * One row of the face dialog's list: the aisle's name, where it ends up
   * (`badge`), and its facts joined as the Sync joins them — zone; for an
   * aisle that moves, the legs it carries and the RackEyes on them; and its
   * uprights, or what they grow to on the Sync.
   */
  #aisleRow(id, badge, { primary = false, grow = null, carried = true } = {}) {
    const texts = this.faceDialogTextsValue ?? {}
    const facts = this.aisleFactsValue?.[id] ?? {}
    const details = []
    if (facts.zone) details.push(facts.zone)
    if (carried) {
      if (typeof facts.legs === "number")
        details.push(fill(texts.legs ?? "", "count", facts.legs))
      const rackEyes = this.#rackEyesOn(id)
      if (rackEyes > 0)
        details.push(fill(texts.rackeyes ?? "", "count", rackEyes))
    }
    const uprights = this.aisleUprightsValue?.[id]
    if (grow) {
      details.push(
        fill(fill(texts.grows ?? "", "from", grow.from), "to", grow.count),
      )
    } else if (typeof uprights === "number") {
      details.push(fill(texts.upright_count ?? "", "count", uprights))
    }
    return { name: this.#aisleName(id), badge: badge ?? "", primary, details }
  }

  /**
   * The RackEyes an aisle on this plan carries: those on its legs. RackEyes
   * pending Sync stand on uprights with no leg and stay where they are.
   */
  #rackEyesOn(id) {
    const at = this.aisleFacesValue?.[id]
    if (!at) return 0
    const run = this.mounted?.planInfo
      ?.read?.()
      ?.runs?.find((candidate) => candidate.id === at.runId)
    return (run?.devices ?? []).filter(
      (device) => device.side === at.side && device.assetId,
    ).length
  }

  /** How many RackEyes on a face wait for a sync (`racking.pendingOn`). */
  #pendingOn(face) {
    return this.mounted?.racking?.pendingOn?.(face.runId, face.side) ?? 0
  }

  /** data-action="click->facilities--floorplan-canvas#confirmFaceChange" */
  confirmFaceChange() {
    const pending = this.#pendingFaceChange
    if (!pending) return

    pending.confirmed = true
    document.getElementById(FACE_DIALOG_ID)?.close()
    this.#serverOp(this.bindFacePathValue, pending.params)
  }

  /**
   * data-action="close->facilities--floorplan-canvas#faceDialogClosed"
   *
   * The dialog closes for every reason — Cancel, Escape, the backdrop, and
   * Confirm. Anything but Confirm puts the select back.
   */
  faceDialogClosed() {
    const pending = this.#pendingFaceChange
    this.#pendingFaceChange = null
    if (pending && !pending.confirmed) pending.revert?.()
    this.#catchUp()
  }

  /** data-action="change->facilities--floorplan-canvas#toggleFace" around a face's switch. */
  toggleFace(event) {
    if (this.#locked) return
    if (this.settingFaceSwitch) return

    const toggle = event.target.closest("[data-racking-face-toggle]")
    const side = toggle?.dataset.rackingFaceToggle
    const selection = this.rackingSelection
    if (!toggle || !side || !selection) return
    if (this.#busy) {
      this.#setFaceSwitch(toggle, !event.target.checked)
      return
    }

    const enabling = event.target.checked
    const managed = selection.aisles
      .filter((aisle) => aisle.managed)
      .map((aisle) => aisle.side)
    const sides = enabling
      ? [...new Set([...managed, side])]
      : managed.filter((face) => face !== side)

    // A rack keeps at least one face (`RackingRun`'s own rule).
    const result =
      sides.length > 0 ? this.mounted?.racking?.setExposedSides(sides) : null
    if (!result || result.blockedLegIds) {
      this.#setFaceSwitch(toggle, !enabling)
      if (result?.blockedLegIds)
        this.#setStatus("edit", this.faceDisableRefusedMessageValue)
      return
    }
    this.#refreshRacking()

    // The plan saves itself a few seconds after any edit; a face is saved at
    // once. A face switched on is one about to take an aisle, and putting it
    // there is the server's change, made only on a plan with nothing unsaved
    // — `bindFace` waits for this save rather than refusing.
    this.#faceSavePending = true
    this.mounted?.save?.requestSave?.()
  }

  /** data-action="mouseenter->facilities--floorplan-canvas#highlightFace" */
  highlightFace(event) {
    const side = event.currentTarget.dataset.rackingAisle
    if (side) this.mounted?.racking?.highlightFace?.(side)
  }

  /** data-action="mouseleave->facilities--floorplan-canvas#unhighlightFace" */
  unhighlightFace() {
    this.mounted?.racking?.highlightFace?.(null)
  }

  // A face switched on or off, saved at once (`toggleFace`), and whoever is
  // waiting for that save to land (`#afterSave`).
  #faceSavePending = false
  #saveWaiters = []

  /**
   * Resolves true once the plan is saved, false if the save failed — or, with
   * `timeoutMs`, if it has not reported by then: a save the editor cannot make
   * (the canvas not ready, the catalogue degraded) says nothing at all, and a
   * waiter left behind would act on the next save, minutes later.
   */
  #afterSave(timeoutMs = null) {
    if (this.saveState === "saved") return Promise.resolve(true)
    return new Promise((resolve) => {
      let timer = null
      const settle = (saved) => {
        clearTimeout(timer)
        resolve(saved)
      }
      this.#saveWaiters.push(settle)
      if (timeoutMs === null) return
      timer = setTimeout(() => {
        this.#saveWaiters = this.#saveWaiters.filter((w) => w !== settle)
        resolve(false)
      }, timeoutMs)
    })
  }

  #nothingUnsaved() {
    if (this.mounted && this.saveState && this.saveState !== "saved") {
      this.#setStatus("edit", this.faceUnsavedMessageValue)
      return false
    }
    return true
  }

  /** Which aisle stands on each face of the plan, as `runId:side` → aisle id. */
  #facesOn() {
    return new Map(
      Object.entries(this.aisleFacesValue ?? {}).map(([id, at]) => [
        `${at.runId}:${at.side}`,
        id,
      ]),
    )
  }

  /**
   * The aisles whose count of uprights grows when `chosen` comes to this face
   * on the Sync (`RegistrationPlan#align_shared_racks`): on this rack, the shorter of it and the aisle
   * on the other face; in a swap, on the rack `chosen` leaves, the shorter of
   * the displaced aisle and the one staying there. Each as the id of the aisle
   * that grows, and from what count to what.
   */
  #growingUprights(runId, side, chosen, current, from) {
    const counts = this.aisleUprightsValue ?? {}
    const faces = this.#facesOn()
    const pairs = []

    // This rack: the chosen aisle and whoever stays on the other face —
    // unless that is the chosen one itself, swapping faces within the rack.
    const partner = faces.get(`${runId}:${oppositeFace(side)}`)
    if (partner && partner !== chosen) pairs.push([chosen, partner])
    // The rack the chosen one leaves, when the displaced aisle goes there.
    if (current && from && from.runId !== runId) {
      const staying = faces.get(`${from.runId}:${oppositeFace(from.side)}`)
      if (staying) pairs.push([current.id, staying])
    }

    const grows = []
    for (const [a, b] of pairs) {
      const count = sharedUprights(counts, a, b)
      if (count === null) continue
      for (const id of [a, b]) {
        if (counts[id] === count) continue
        grows.push({ id, from: counts[id], count })
      }
    }

    // The racks the change reshapes keep their RackEyes waiting for a sync,
    // and their aisles grow to reach them (`#heldGrowth`).
    const held = this.#reshapedRacks(
      runId,
      side,
      chosen,
      current,
      from,
    ).flatMap((rack) =>
      this.#heldGrowth(rack.runId, rack.aisleIds, rack.leaving),
    )
    for (const grow of held) {
      const same = grows.find((other) => other.id === grow.id)
      if (!same) grows.push(grow)
      else if (grow.count > same.count) same.count = grow.count
    }
    return grows
  }

  /**
   * The racks a face change reshapes (`Floorplans::BindFace` `@reshaped`),
   * each with the aisles standing on it after the change and the faces whose
   * aisle leaves it: this one — where the displaced aisle leaves, unless it
   * only changes face — and the one the chosen aisle leaves, where the
   * displaced one may land.
   */
  #reshapedRacks(runId, side, chosen, current, from) {
    const faces = this.#facesOn()
    const within = from?.runId === runId
    const partner = faces.get(`${runId}:${oppositeFace(side)}`)
    const here = [chosen]
    if (within) {
      if (current) here.push(current.id)
    } else if (partner && partner !== chosen) {
      here.push(partner)
    }
    const racks = [
      { runId, aisleIds: here, leaving: current && !within ? [side] : [] },
    ]
    if (from && !within) {
      const staying = faces.get(`${from.runId}:${oppositeFace(from.side)}`)
      racks.push({
        runId: from.runId,
        aisleIds: [current?.id, staying].filter(Boolean),
        leaving: [from.side],
      })
    }
    return racks
  }

  /**
   * Whether a face change drops the length a person asked for a rack it
   * reshapes (`plannedUprights`): it had one, and the rack ends another
   * length on the Sync — the one its RackEyes waiting hold it at, or else
   * the count its aisles share. One they reach anyway is met, not lost. The
   * server does the same (`BindFace` `lengths_dropped`) and says so after;
   * said here first.
   */
  #dropsLength({ runId, aisleIds, leaving }) {
    const planned = this.mounted?.racking?.plannedUprights?.() ?? {}
    if (planned[runId] === undefined) return false

    const held = this.#heldGrowth(runId, aisleIds, leaving)
    if (held.length > 0) return held[0].count !== planned[runId]
    const counts = this.aisleUprightsValue ?? {}
    const known = aisleIds
      .map((id) => counts[id])
      .filter((count) => typeof count === "number")
    return known.length === 0 || Math.max(...known) !== planned[runId]
  }

  /**
   * What a face change holds a rack it reshapes at for its RackEyes waiting
   * for a sync (`Floorplans::RunMirror` `keep_pinned`): each of `aisleIds`
   * shorter than the furthest one grows to it on the Sync — said here, as the
   * server says it after the change (`BindFace#held_uprights`). The legs on
   * `leaving` faces go with their aisle.
   */
  #heldGrowth(runId, aisleIds, leaving = []) {
    const counts = this.aisleUprightsValue ?? {}
    const known = aisleIds.filter((id) => typeof counts[id] === "number")
    if (known.length === 0) return []

    const reach = this.mounted?.racking?.heldReach?.(runId, leaving) ?? 0
    if (reach <= Math.max(...known.map((id) => counts[id]))) return []
    return known
      .filter((id) => counts[id] < reach)
      .map((id) => ({ id, from: counts[id], count: reach }))
  }

  /**
   * An aisle's own name, as facilities has it. Its zone is said apart, in its
   * row of the dialog — the select's label, which also carries the zone, only
   * for an aisle with no facts.
   */
  #aisleName(id) {
    const name = this.aisleFactsValue?.[id]?.name
    if (name) return name
    const select = this.element.querySelector("[data-racking-face-select]")
    return select ? optionLabel(select, id) : ""
  }

  /**
   * `kind` picks the title and the button (bind, move, swap, separate);
   * `message` the sentence saying what moves where; `rows` one per aisle the
   * change moves or grows (`#aisleRow`); `notes` one line each for what else
   * happens — a rack left empty, RackEyes pending Sync staying behind.
   */
  #openFaceDialog(kind, message, names, rows, notes, params, revert) {
    const dialog = document.getElementById(FACE_DIALOG_ID)
    if (!dialog) return revert?.()

    const texts = this.faceDialogTextsValue ?? {}
    const say = (template, values) =>
      Object.entries(values).reduce(
        (text, [key, value]) => fill(text, key, value),
        template ?? "",
      )
    const write = () => {
      dialog.querySelector(`#${FACE_DIALOG_ID}-title`).textContent = say(
        texts[`title_${kind}`],
        names,
      )
      dialog
        .querySelector(`#${FACE_DIALOG_ID}-description`)
        .replaceChildren(...sentence(texts[`message_${message}`], names))
      const changes = dialog.querySelector("[data-face-dialog-changes]")
      const list = dialog.querySelector("[data-face-dialog-rows]")
      const template = dialog.querySelector("[data-face-dialog-row-template]")
      if (changes && list && template) {
        list.replaceChildren(...rows.map((row) => faceDialogRow(template, row)))
        changes.hidden = rows.length === 0
      }
      const box = dialog.querySelector("[data-face-dialog-notes]")
      if (box) {
        const lines = box.querySelector("[data-face-dialog-note-list]") ?? box
        lines.replaceChildren(
          ...notes.map(([key, values]) => {
            const line = document.createElement("li")
            line.replaceChildren(...sentence(texts[key], values))
            return line
          }),
        )
        box.hidden = notes.length === 0
      }
      // What else the change does is part of what the dialog says: read out
      // with its message, not left for somebody who can see it.
      dialog.setAttribute(
        "aria-describedby",
        [
          `${FACE_DIALOG_ID}-description`,
          rows.length > 0 ? `${FACE_DIALOG_ID}-changes` : null,
          notes.length > 0 ? `${FACE_DIALOG_ID}-notes` : null,
        ]
          .filter(Boolean)
          .join(" "),
      )
      const confirm = dialog.querySelector("[data-face-dialog-confirm]")
      if (confirm) confirm.textContent = texts[`confirm_${kind}`] ?? ""
    }

    this.#showDialog(dialog, { params, revert, confirmed: false }, write)
  }

  /**
   * Open one of the design system's dialogs through its own `ui--modal`
   * controller. Cancel, Escape and the backdrop close it through that
   * controller, which fades the panel out and leaves it at `opacity: 0`; only
   * its `open()` fades it back in, so a dialog opened with a bare
   * `showModal()` after one cancel is open, modal — and invisible.
   *
   * That controller closes AFTER its fade, so a dialog asked for again while
   * the last one is still fading is opened once it has closed: that close
   * settles the change it was about (`faceDialogClosed`), never this one —
   * which is why the face change waiting here (`pending`), and its words
   * (`write`), are only taken on when it is shown.
   */
  #showDialog(dialog, pending = null, write = null) {
    if (!dialog) return
    if (dialog.open) {
      dialog.addEventListener(
        "close",
        () => this.#showDialog(dialog, pending, write),
        {
          once: true,
        },
      )
      return
    }

    write?.()
    if (pending) this.#pendingFaceChange = pending
    const modal = this.application.getControllerForElementAndIdentifier(
      dialog,
      "ui--modal",
    )
    if (modal) modal.open()
    else dialog.showModal()
  }

  // A change the server makes to the plan, posted at most one at a time —
  // or the plan fetched after somebody else's (`#refreshPlan`).
  #busy = false
  // Posting one of ours: the editor is inert, and its keys go nowhere.
  #posting = false
  #pendingFaceChange = null
  // A newer plan announced while it could not be fetched (`#planSynced`).
  #missedVersion = null
  // Whoever waits for the one in flight to finish (`#whenIdle`).
  #idleWaiters = []

  /**
   * Post a change the server makes to the plan and apply its answer — the
   * plan as it now is — in place. While it is out the editor is busy: nothing
   * else is posted, and the `plan_synced` this very change announces does not
   * fetch the plan a second time. One confirmed while another is out waits
   * its turn rather than being dropped.
   */
  async #serverOp(url, body) {
    if (!url) return null
    while (this.#busy) await this.#whenIdle()
    this.#busy = true
    this.#posting = true
    this.#setBusy(true)
    try {
      const response = await post(url, { body, responseKind: "json" })
      const answer = await response.json.catch(() => null)
      if (!response.ok) {
        this.#setStatus(
          "edit",
          answer?.notice?.message ?? this.loadErrorMessageValue,
        )
        this.#refreshRacking()
        return { status: response.statusCode }
      }
      this.#adopt(answer)
      if (answer?.notice?.message)
        this.#setStatus("edit", answer.notice.message)
      return { status: response.statusCode }
    } catch {
      this.#setStatus("edit", this.loadErrorMessageValue)
      this.#refreshRacking()
      return null
    } finally {
      this.#posting = false
      this.#setBusy(false)
      this.#settle()
    }
  }

  /** Resolves once nothing is in flight. */
  #whenIdle() {
    return new Promise((resolve) => this.#idleWaiters.push(resolve))
  }

  /**
   * The one in flight is done: the next one waiting goes — its answer is the
   * plan as it then is, so nothing need be fetched — or, with none waiting,
   * a plan announced meanwhile is fetched.
   */
  #settle() {
    this.#busy = false
    const waiting = this.#idleWaiters
    this.#idleWaiters = []
    if (waiting.length > 0) {
      for (const resolve of waiting) resolve()
      return
    }
    this.#catchUp()
  }

  /** A face change or Sync being asked in its dialog. */
  #askingPlanChange() {
    return (
      Boolean(this.#pendingFaceChange) ||
      Boolean(document.getElementById(SYNC_DIALOG_ID)?.open)
    )
  }

  /** Fetch the plan announced while it could not be, if it is still newer. */
  #catchUp() {
    const missed = this.#missedVersion
    if (missed === null || this.#busy || this.#askingPlanChange()) return

    this.#missedVersion = null
    this.#planSynced(missed)
  }

  /** The editor greyed out and inert while the server changes the plan. */
  #setBusy(busy) {
    if (this.hasStageTarget) this.stageTarget.inert = busy
    if (this.hasRackingPanelTarget) {
      this.rackingPanelTarget.setAttribute("aria-busy", String(busy))
      this.rackingPanelTarget.inert = busy
    }
  }

  /**
   * The plan as the server now has it, applied where the editor stands
   * (`adoptPlan`): its racks and RackEyes, its version, and what the panels
   * know about which aisle stands where. A plan that will not apply in place
   * — which should not happen — is reloaded, the one case that still does.
   */
  #adopt(answer) {
    if (!answer || !this.mounted) return null

    this.mounted.racking?.highlightFace?.(null)
    const result = this.mounted.adoptPlan({
      contentVersion: answer.content_version,
      runs: answer.runs ?? [],
      devices: answer.devices ?? [],
      // Only with the tenant's barriers on: absent, they are left alone.
      barriers: answer.barriers,
    })
    if (!result?.adopted) {
      if (result?.reason === "invalid") this.reloadPlan()
      else if (result?.reason === "unsaved")
        this.#setStatus("edit", this.planSyncedMessageValue)
      // The editor already holds a newer version than this answer (a device
      // fitted meanwhile): the plan is fetched as it now is, once settled.
      else if (result?.reason === "stale") this.#missedVersion = Infinity
      return result
    }

    this.contentVersionValue = answer.content_version
    this.runFacilitiesValue = answer.run_facilities ?? {}
    if (answer.aisle_faces) this.aisleFacesValue = answer.aisle_faces
    if (answer.aisle_uprights) this.aisleUprightsValue = answer.aisle_uprights
    if (answer.aisle_facts) this.aisleFactsValue = answer.aisle_facts
    if (answer.leg_history) this.legHistoryValue = answer.leg_history
    // A Sync that registered the selected barrier locks its name and zone at
    // once (CONEK-8870).
    if (answer.linked_barriers) {
      this.linkedBarriersValue = answer.linked_barriers
      const barrier = this.mounted.barrierPanel?.state()
      if (barrier) this.#publishBarrierState(barrier)
    }
    // The floor's zones, which facilities may have changed meanwhile: zones
    // added after the page loaded give the select its choice.
    if (answer.zone_options) {
      const zone = this.element.querySelector("[data-racking-zone-select]")
      if (zone) setFlatOptions(zone, answer.zone_options)
    }
    if (answer.aisle_options) {
      for (const select of this.element.querySelectorAll(
        "[data-racking-face-select]",
      )) {
        setSelectOptions(select, answer.aisle_options)
      }
    }

    if (answer.select_run_id) {
      this.mounted.properties?.select?.(answer.select_run_id)
    }
    this.#refreshRacking()
    this.#publishInfo()
    this.#renderSyncBadge()
    this.#loadDeviceStatus()
    return result
  }

  /** Another editor changed the plan: fetch it and apply it, if nothing here is unsaved. */
  async #refreshPlan() {
    if (this.#busy || !this.hasPlanStatePathValue) return
    this.#busy = true
    try {
      const response = await get(this.planStatePathValue, {
        responseKind: "json",
      })
      const result = response.ok ? this.#adopt(await response.json) : null
      // Applied in place, so nothing on screen jumps — which is exactly why
      // it is said: racks moved under somebody's eyes without a word.
      if (result?.adopted && result.changed > 0) {
        this.#setStatus("edit", this.planChangedElsewhereMessageValue)
      }
    } catch {
      // A plan that could not be fetched is fetched on the next change.
    } finally {
      this.#settle()
    }
  }

  // ── The right rail ────────────────────────────────────────────────────────
  //
  // Every open and close of a right-hand panel goes through these. See
  // `RIGHT_RAIL` at the top of this file for what is in the rail and why.

  /**
   * THE ONLY WRITER of a rail panel's `hidden`, and of its button's
   * `data-active` / `aria-pressed`. The panel's own `hidden` is the state —
   * read back rather than remembered, because two places remembering whether
   * a panel is open is two places to disagree.
   */
  #applyPanel(entry, open) {
    const panel = this.#railTarget(entry.panel)
    if (panel) panel.hidden = !open

    const button = entry.button && this.#railTarget(entry.button)
    if (button) this.#setActive(button, open)

    // The contents are read only while somebody can see them — see
    // `#watchInfo`. After `hidden`, so the first answer finds the panel open.
    if (entry.key === "info") this.#watchInfo(open)
  }

  /** A toolbar press. The panel's own `hidden` is what says which way. */
  #togglePanel(key) {
    const entry = RIGHT_RAIL.find((candidate) => candidate.key === key)
    const panel = entry && this.#railTarget(entry.panel)
    if (!panel) return

    if (panel.hidden) this.#openPanel(key)
    else this.#closePanel(key)
  }

  /** Open one panel and close every other. */
  #openPanel(key) {
    for (const entry of RIGHT_RAIL) this.#applyPanel(entry, entry.key === key)
    this.#syncRail()
  }

  /**
   * Close ONE panel, and only that one — never "every selection panel": the
   * selection seams fire independently, and a null from one must not shut a
   * panel another has just opened.
   */
  #closePanel(key) {
    const entry = RIGHT_RAIL.find((candidate) => candidate.key === key)
    if (entry) this.#applyPanel(entry, false)
    this.#syncRail()
  }

  /**
   * A SELECTION changed — not a re-render of one. A selection takes the rail;
   * clearing it closes only its own panel, so a click on empty floor does not
   * shut a panel somebody opened on purpose.
   */
  #selectPanel(key, shown) {
    if (shown) this.#openPanel(key)
    else this.#closePanel(key)
  }

  /**
   * A rail target, or null when the panel is not on the page. Stimulus raises
   * on a missing target, so `?.` alone would not save a panel that is absent.
   */
  #railTarget(name) {
    const has = `has${name[0].toUpperCase()}${name.slice(1)}Target`
    return this[has] ? this[`${name}Target`] : null
  }

  /**
   * `data-rail` on the row: "a panel is open over the plan right now".
   * Derived from the DOM, for the same reason nothing here remembers whether a
   * panel is open.
   */
  #syncRail() {
    if (!this.hasRailTarget) return

    const open = RIGHT_RAIL.some(
      (entry) => this.#railTarget(entry.panel)?.hidden === false,
    )
    this.railTarget.dataset.rail = open ? "open" : "closed"
  }

  // ── Boot and zoom ─────────────────────────────────────────────────────────

  /**
   * How far the boot got, for the loading overlay. The overlay is a separate
   * controller, so this travels as a document event; dispatching into a page
   * without one is harmless.
   */
  #bootStep(step) {
    this.dispatch("boot-step", {
      prefix: "canvas",
      detail: { step },
      bubbles: true,
    })
  }

  #bootFailed() {
    this.dispatch("boot-failed", { prefix: "canvas", bubbles: true })
  }

  /**
   * The two milestones only the ENGINE knows, replayed out of the seam — both
   * are emitted inside `init()`, before this controller could have subscribed.
   */
  #wireBoot() {
    this.unsubscribeBoot = this.mounted?.boot?.onStep((step) => {
      this.#bootStep(step)
    })
  }

  /** The number only; the label and the `%` are in the ERB beside it. */
  #renderZoom(percentage) {
    if (!this.hasZoomLevelTarget) return

    this.zoomLevelTarget.textContent = String(percentage)
  }

  // One line, several sources, each owning its own slot. Writing straight to
  // the element meant the save cycle blanked it every few seconds — see
  // `status_messages.js` for why that is worse than saying nothing.
  #setStatus(source, message) {
    this.statusMessages[source] = message
    if (!this.hasStatusTarget) return

    const text = mostSeriousMessage(this.statusMessages)

    this.statusTarget.textContent = text
    if (this.hasStatusBoxTarget) hideOrShow(this.statusBoxTarget, text === "")
  }

  /**
   * Read-only: for somebody who may only look, and for everybody else until
   * they start editing. Every handler here that changes the plan returns
   * while it holds, not only the canvas: the panels' controls are hidden then
   * (`data-floorplan-edit-only`), and they change the scene without asking
   * the canvas's own lock.
   */
  get #locked() {
    return this.readOnlyValue || !this.editingValue
  }

  /** The engine's last save status, as it said it (`#renderSaveStatus`). */
  #saveStatus = null

  /** data-action="click->facilities--floorplan-canvas#startEditing" */
  startEditing() {
    this.#setEditing(true)
  }

  /** data-action="click->facilities--floorplan-canvas#stopEditing" */
  stopEditing() {
    this.#setEditing(false)
  }

  /**
   * Start or stop editing the plan where it stands: the canvas unlocks or
   * locks in place (`setEditing`), the chrome only editing uses shows or hides
   * (`data-floorplan-mode`), and the URL keeps the mode for a reload.
   *
   * Stopping is refused while a change is unsaved or a save is out, and says
   * so: nothing is saved on anybody's behalf.
   */
  #setEditing(editing) {
    if (this.readOnlyValue || !this.mounted) return
    if (editing === this.editingValue) return

    if (!this.mounted.setEditing(editing)) {
      this.#setStatus("edit", this.modeSaveFirstMessageValue)
      return
    }

    this.editingValue = editing
    this.element.dataset.floorplanMode = editing ? "edit" : "view"
    if (!editing) this.#showLayersTab()
    this.#writeModeToUrl(editing)
    // The save state as it really is while editing, and none while looking.
    if (this.#saveStatus) this.#renderSaveStatus(this.#saveStatus)
    this.#setStatus("edit", "")
  }

  /**
   * The sidebar's layers, the tab shown while the plan is only looked at —
   * its tab bar is hidden then (`data-floorplan-tabs-edit-only`). Asked of
   * the tabs themselves, so the tab and the URL they keep agree.
   */
  #showLayersTab() {
    const tab = this.element.querySelector(
      "[data-floorplan-tabs-edit-only] [role='tab'][data-tab-key='entities']",
    )
    if (tab && tab.getAttribute("aria-selected") !== "true") tab.click()
  }

  /**
   * `?mode=edit` while editing, so a reload opens the plan edited again; gone
   * once it is only looked at. `replaceState`, as the view's own keys are
   * written (`view_mode_controller`), and only this key is touched.
   */
  #writeModeToUrl(editing) {
    const url = new URL(window.location.href)
    if (editing) url.searchParams.set("mode", "edit")
    else url.searchParams.delete("mode")
    window.history.replaceState(window.history.state, "", url)
  }

  /** data-action="click->facilities--floorplan-canvas#dismissStatus" */
  dismissStatus() {
    for (const source of Object.keys(this.statusMessages)) {
      this.statusMessages[source] = ""
    }
    this.#setStatus("edit", "")
  }
}

/**
 * Hand the browser a file to save. The link is clicked and gone at once; its
 * object URL is let go a moment later — at once can cancel the download.
 */
function saveFile(blob, filename) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.hidden = true
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

/**
 * Show or hide an element that also carries a display utility.
 *
 * An inline style is the only thing that reliably beats a Tailwind `flex` or
 * `inline-flex`; the `hidden` ATTRIBUTE loses to it, because `[hidden]` lives in
 * the base layer and utilities come after. `""` restores whatever the class
 * said rather than guessing a display value back.
 */
/** The slot the barrier toast's template leaves for the reason. */
const BARRIER_TOAST_REASON = "%{reason}"

/** The one barrier toast on screen; a new one replaces it. */
const BARRIER_TOAST_ID = "floorplan-barrier-toast"

function hideOrShow(element, hide) {
  element.hidden = hide
  element.style.display = hide ? "none" : ""
}
